/**
 * Elite Batch 6 Part 4 — Workflow Templates 508–527
 * Returning-Customer Recovery, Returns Processing, Rewards, SKU Normalization,
 * SMS Distribution, Sales Rep Assist, Satisfaction-to-Review, Seasonal Replenishment,
 * Second-Purchase Accelerator, Session Abandonment, Shipping Bots, Shopify Checkout,
 * Cart Optimization, Size Guide Chatbots
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function zendeskNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  body: string,
  priority: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Bearer {zendesk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: `{"subject":"${subject}","comment":{"body":"${body}"},"priority":"${priority}"}`,
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}
function salesforceNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{sf_instance}.salesforce.com/services/data/v59.0/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {salesforce_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 6 PART 4 — TEMPLATES 508–527
// =============================================================================

export const ELITE_BATCH6_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 508. RETURNING-CUSTOMER CART RECOVERY (8 nodes)
  // ===================================================================

  "Returning-Customer Cart Recovery": {
    summary:
      "Recovers abandoned carts by routing recovery messaging based on loyalty tier. A cart abandonment webhook triggers AI extraction of cart and customer data, loyalty profile matching via API, tier routing condition, non-member acquisition email, standard points-based email, VIP concierge email, VIP SMS via Twilio, and tier recovery logging to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandoned by returning customer", "webhook_app", {
        source: "E-commerce Platform / Shopify",
        event: "checkout.abandoned",
        howItStarts:
          "A customer with prior purchase history abandons their shopping cart, triggering tier-aware recovery",
        setup:
          "Connect your e-commerce platform to POST cart abandonment events (cart contents, customer email, session data) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extraction + loyalty profile match
      aiNode(
        "2",
        "AI extract cart & customer data",
        280,
        "You are a cart recovery specialist. Parse the abandoned cart webhook payload and extract customer identity, cart contents, and any loyalty identifiers regardless of format. Look for email, customer ID, cart items with names and prices, total value, loyalty member ID or tier if present. Respond JSON: {customer_email: string, customer_name: string, customer_id: string, cart_items_summary: string, cart_total: number, item_count: number, top_item_name: string, top_item_price: number, loyalty_member_id: string, has_loyalty_id: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
          {
            field: "cart_items_summary",
            as: "cart_items_summary",
            type: "string",
          },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "top_item_name", as: "top_item_name", type: "string" },
          { field: "top_item_price", as: "top_item_price", type: "number" },
          {
            field: "loyalty_member_id",
            as: "loyalty_member_id",
            type: "string",
          },
          { field: "has_loyalty_id", as: "has_loyalty_id", type: "string" },
        ],
      ),

      // 3. Loyalty profile pull
      n("3", "hitApi", "Pull loyalty profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{loyalty_api}/api/v1/members/{loyalty_member_id}?include=tier,points,lifetime_value",
          headers: [
            { key: "Authorization", value: "Bearer {loyalty_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data.tier", customName: "loyalty_tier", type: "string" },
            {
              name: "data.points_balance",
              customName: "points_balance",
              type: "number",
            },
            {
              name: "data.lifetime_value",
              customName: "lifetime_value",
              type: "number",
            },
            {
              name: "data.phone",
              customName: "customer_phone",
              type: "string",
            },
          ],
        },
      }),

      // 4. Tier routing condition
      n("4", "condition", "Is VIP tier member?", 760, {
        conditionData: [
          {
            variable: "loyalty_tier",
            value: "vip",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "loyalty_tier",
            value: "platinum",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. Non-member acquisition email (False path — not VIP, check if member at all)
      emailNode(
        "5",
        "Non-member / Standard: Points recovery email",
        1000,
        "{customer_email}",
        "You left {top_item_name} behind — earn points when you complete your order!",
        "Hi {customer_name},\\n\\nYour cart with {item_count} item(s) worth ${cart_total} is waiting for you.\\n\\nItems: {cart_items_summary}\\n\\nComplete your purchase and earn loyalty points on every dollar spent. Your current balance: {points_balance} points.\\n\\nReturn to your cart: https://{store_url}/cart/recover\\n\\nHappy shopping!",
        400,
      ),

      // 6. VIP concierge email (True path)
      emailNode(
        "6",
        "VIP: Concierge recovery email",
        1000,
        "{customer_email}",
        "{customer_name}, your personal shopper saved your selections",
        "Dear {customer_name},\\n\\nAs a valued {loyalty_tier} member, we noticed you were browsing some wonderful items.\\n\\nYour selections ({item_count} items, ${cart_total}):\\n{cart_items_summary}\\n\\nWe have reserved these items for you for the next 48 hours. As a {loyalty_tier} member, you also have {points_balance} points available — enough for a meaningful discount.\\n\\nResume your order: https://{store_url}/cart/recover?vip=true\\n\\nYour Personal Shopper",
      ),

      // 7. VIP SMS via Twilio
      smsNode(
        "7",
        "SMS VIP cart reminder (Twilio)",
        1240,
        "{customer_phone}",
        "Hi {customer_name}, your {loyalty_tier} cart (${cart_total}) is reserved for 48h. You have {points_balance} points to use! Complete your order: https://{store_url}/cart/recover",
      ),

      // 8. Tier recovery logging (Sheets)
      sheetsNode(
        "8",
        "Log tier recovery to Sheets",
        1480,
        "CartRecovery",
        '[["{customer_email}","{customer_name}","{loyalty_tier}","{cart_total}","{item_count}","{top_item_name}","{points_balance}","{lifetime_value}","{has_loyalty_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"), // VIP -> concierge email
      e("4", "5", "output_2"), // Not VIP -> standard/acquisition email
      e("6", "7"), // VIP also gets SMS
      e("5", "8"), // standard path -> logging
      e("7", "8"), // VIP path -> logging
    ],
  },

  // ===================================================================
  // 509. RETURNS ANALYTICS DASHBOARD UPDATER (8 nodes)
  // ===================================================================

  "Returns Analytics Dashboard Updater": {
    summary:
      "Maintains a returns analytics dashboard with anomaly detection. A scheduled trigger fetches return activity data, aggregates metrics via runCode, checks for anomalies against baselines, updates a Google Sheets dashboard, emails QA/product teams on trends, sends urgent Slack alerts for spikes, and logs trend history for longitudinal analysis.",
    nodes: [
      trigger("Schedule: Hourly returns analytics refresh", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to aggregate return activity, detect anomalies, and update the returns dashboard",
        setup:
          "Configure the schedule to match your returns monitoring cadence. Ensure the returns API and Google Sheets credentials are connected.",
        inputFields: [],
      }),

      // 2. Return activity fetch
      n("2", "hitApi", "Fetch return activity data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{returns_api}/api/v1/returns?period=last_24h&include=reasons,products,values",
          headers: [
            { key: "Authorization", value: "Bearer {returns_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.total_returns",
              customName: "total_returns",
              type: "number",
            },
            {
              name: "data.total_value",
              customName: "return_value",
              type: "number",
            },
            {
              name: "data.top_reason",
              customName: "top_reason",
              type: "string",
            },
            {
              name: "data.top_product_sku",
              customName: "top_return_sku",
              type: "string",
            },
            {
              name: "data.top_product_name",
              customName: "top_return_product",
              type: "string",
            },
            {
              name: "data.return_rate_pct",
              customName: "return_rate",
              type: "number",
            },
            {
              name: "data.avg_return_value",
              customName: "avg_return_value",
              type: "number",
            },
          ],
        },
      }),

      // 3. Aggregation and anomaly scoring (runCode)
      n("3", "runCode", "Aggregate & score anomalies", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate return metrics and score anomalies against baseline
const totalReturns = parseInt(vars.total_returns || "0", 10);
const returnRate = parseFloat(vars.return_rate || "0");
const returnValue = parseFloat(vars.return_value || "0");
const avgValue = parseFloat(vars.avg_return_value || "0");
// Baselines (configurable)
const baselineRate = 5.0;  // 5% return rate baseline
const baselineDaily = 50;  // 50 returns/day baseline
const hourlyExpected = baselineDaily / 24;
const rateDeviation = returnRate - baselineRate;
const volumeDeviation = totalReturns - hourlyExpected;
const anomalyScore = Math.max(0, (rateDeviation * 10) + (volumeDeviation * 2));
const isAnomaly = anomalyScore >= 20 || returnRate >= (baselineRate * 1.5);
const severity = anomalyScore >= 50 ? "critical" : anomalyScore >= 20 ? "warning" : "normal";
return {
  anomaly_detected: isAnomaly ? "yes" : "no",
  anomaly_score: Math.round(anomalyScore),
  severity: severity,
  rate_deviation: rateDeviation.toFixed(2),
  volume_deviation: Math.round(volumeDeviation),
  hourly_expected: Math.round(hourlyExpected),
  dashboard_summary: totalReturns + " returns ($" + returnValue.toFixed(0) + ") | Rate: " + returnRate.toFixed(1) + "% | Top reason: " + (vars.top_reason || "N/A") + " | Anomaly: " + severity
};`,
          outputVariables: [
            { name: "anomaly_detected", type: "string" },
            { name: "anomaly_score", type: "number" },
            { name: "severity", type: "string" },
            { name: "rate_deviation", type: "string" },
            { name: "volume_deviation", type: "number" },
            { name: "hourly_expected", type: "number" },
            { name: "dashboard_summary", type: "string" },
          ],
        },
      }),

      // 4. Baseline anomaly condition
      n("4", "condition", "Anomaly detected?", 760, {
        conditionData: [
          {
            variable: "anomaly_detected",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Dashboard update (Sheets) — always runs
      sheetsNode(
        "5",
        "Update returns dashboard (Sheets)",
        1000,
        "ReturnsDashboard",
        '[["{total_returns}","{return_value}","{return_rate}","{top_reason}","{top_return_sku}","{top_return_product}","{avg_return_value}","{anomaly_score}","{severity}","{dashboard_summary}"]]',
      ),

      // 6. QA/Product email (True path — anomaly)
      emailNode(
        "6",
        "Email QA/product team on anomaly",
        1240,
        "{qa_team_email}",
        "Returns Anomaly Alert [{severity}]: {return_rate}% return rate detected",
        "Returns Anomaly Report\\n\\nSeverity: {severity} (score: {anomaly_score})\\nReturn Rate: {return_rate}% (baseline deviation: {rate_deviation}%)\\nVolume: {total_returns} returns this hour (expected: {hourly_expected})\\nTotal Value: ${return_value}\\n\\nTop Return Reason: {top_reason}\\nTop Returned Product: {top_return_product} ({top_return_sku})\\nAvg Return Value: ${avg_return_value}\\n\\nPlease investigate and take action if this trend continues.",
        400,
      ),

      // 7. Urgent Slack alert (True path — anomaly)
      slackNode(
        "7",
        "Slack: Urgent returns anomaly alert",
        1480,
        ":warning: Returns Anomaly [{severity}]\\nRate: {return_rate}% (deviation: {rate_deviation}%)\\nVolume: {total_returns} returns (${return_value})\\nTop reason: {top_reason}\\nTop product: {top_return_product}\\nAnomaly score: {anomaly_score}\\nDashboard: {dashboard_summary}",
        400,
      ),

      // 8. Trend history logging
      sheetsNode(
        "8",
        "Log trend history to Sheets",
        1720,
        "ReturnsTrend",
        '[["{total_returns}","{return_value}","{return_rate}","{top_reason}","{top_return_sku}","{anomaly_detected}","{anomaly_score}","{severity}","{rate_deviation}","{volume_deviation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"), // anomaly -> QA email
      e("4", "5", "output_2"), // no anomaly -> dashboard update only
      e("6", "7"), // anomaly -> also Slack
      e("5", "8"), // dashboard -> trend logging
      e("7", "8"), // Slack -> trend logging
    ],
  },

  // ===================================================================
  // 510. RETURNS PROCESSING WORKFLOW (9 nodes)
  // ===================================================================

  "Returns Processing Workflow": {
    summary:
      "End-to-end returns processing from warehouse receipt to customer resolution. A warehouse receipt webhook triggers RMA matching, inspection condition routing, an inspection hold delay, pass/fail routing, refund or replacement processing via Stripe/Shopify, restock or write-off decision, customer resolution email, and SLA audit logging to Sheets.",
    nodes: [
      trigger("Webhook: Warehouse return receipt scanned", "webhook_app", {
        source: "Warehouse Management System",
        event: "return.received",
        howItStarts:
          "A returned package is scanned at the warehouse dock, triggering inspection and processing",
        setup:
          "Connect your WMS to POST return receipt events (tracking number, RMA number, package condition, customer ID, order ID) to the workflow URL.",
        inputFields: [],
      }),

      // 2. RMA matching & order lookup
      aiNode(
        "2",
        "AI match RMA & extract return details",
        280,
        "You are a returns processing specialist. Parse the warehouse receipt payload and match it to an RMA. Extract the RMA number, original order ID, customer email, customer name, product SKU, product name, return reason, package condition on receipt (damaged/good/opened), and declared return value. Respond JSON: {rma_number: string, order_id: string, customer_email: string, customer_name: string, product_sku: string, product_name: string, return_reason: string, package_condition: string, declared_value: number, received_timestamp: string}",
        "{webhook_payload}",
        [
          { field: "rma_number", as: "rma_number", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_sku", as: "product_sku", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "return_reason", as: "return_reason", type: "string" },
          {
            field: "package_condition",
            as: "package_condition",
            type: "string",
          },
          { field: "declared_value", as: "declared_value", type: "number" },
          {
            field: "received_timestamp",
            as: "received_timestamp",
            type: "string",
          },
        ],
      ),

      // 3. Inspection condition — does the package need detailed inspection?
      n("3", "condition", "Package condition requires inspection?", 520, {
        conditionData: [
          {
            variable: "package_condition",
            value: "damaged",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "package_condition",
            value: "opened",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 4. Inspection hold delay (True path — needs inspection)
      n("4", "delayAction", "Hold for physical inspection (24h)", 760, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 5. Pass/fail routing after inspection
      n("5", "runCode", "Determine refund vs replacement vs reject", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine resolution based on return reason and package condition
const condition = vars.package_condition || "good";
const reason = (vars.return_reason || "").toLowerCase();
const value = parseFloat(vars.declared_value || "0");
let resolution, refund_amount, restock_action;
if (condition === "damaged" && reason.includes("defect")) {
  resolution = "full_refund";
  refund_amount = value;
  restock_action = "write_off";
} else if (condition === "good" && (reason.includes("wrong") || reason.includes("size") || reason.includes("fit"))) {
  resolution = "replacement";
  refund_amount = 0;
  restock_action = "restock";
} else if (condition === "good") {
  resolution = "full_refund";
  refund_amount = value;
  restock_action = "restock";
} else if (condition === "opened") {
  resolution = "partial_refund";
  refund_amount = Math.round(value * 0.85 * 100) / 100;
  restock_action = "refurbish";
} else {
  resolution = "review_required";
  refund_amount = 0;
  restock_action = "hold";
}
return {
  resolution: resolution,
  refund_amount: refund_amount,
  restock_action: restock_action,
  needs_refund: resolution.includes("refund") ? "yes" : "no",
  resolution_summary: resolution.replace(/_/g, " ") + " — $" + refund_amount + " | Inventory: " + restock_action
};`,
          outputVariables: [
            { name: "resolution", type: "string" },
            { name: "refund_amount", type: "number" },
            { name: "restock_action", type: "string" },
            { name: "needs_refund", type: "string" },
            { name: "resolution_summary", type: "string" },
          ],
        },
      }),

      // 6. Refund or replacement processing (Stripe refund / Shopify exchange)
      stripeNode(
        "6",
        "Process Stripe refund",
        1240,
        "POST",
        "refunds",
        [
          { key: "charge", value: "{stripe_charge_id}" },
          { key: "amount", value: "{refund_amount_cents}" },
          { key: "reason", value: "requested_by_customer" },
          { key: "metadata[rma]", value: "{rma_number}" },
          { key: "metadata[order_id]", value: "{order_id}" },
        ],
        [
          { name: "id", customName: "refund_id", type: "string" },
          { name: "status", customName: "refund_status", type: "string" },
          { name: "amount", customName: "refunded_amount", type: "number" },
        ],
      ),

      // 7. Restock/writeoff decision (Shopify inventory update)
      shopifyNode(
        "7",
        "Update inventory: restock or write-off",
        1480,
        "POST",
        "inventory_levels/adjust.json",
        [
          { key: "inventory_item_id", value: "{inventory_item_id}" },
          { key: "location_id", value: "{warehouse_location_id}" },
          { key: "available_adjustment", value: "{restock_adjustment}" },
        ],
        [
          {
            name: "inventory_level.available",
            customName: "updated_inventory",
            type: "number",
          },
        ],
      ),

      // 8. Customer resolution email
      emailNode(
        "8",
        "Send customer resolution email",
        1720,
        "{customer_email}",
        "Your return {rma_number} has been processed — {product_name}",
        "Hi {customer_name},\\n\\nYour return for {product_name} (RMA: {rma_number}) has been processed.\\n\\nResolution: {resolution_summary}\\n\\nRefund amount: ${refund_amount}\\nRefund ID: {refund_id}\\nRefund status: {refund_status}\\n\\nIf you have any questions about your return, please reference RMA {rma_number} when contacting support.\\n\\nThank you for your patience.",
      ),

      // 9. SLA audit logging (Sheets)
      sheetsNode(
        "9",
        "Log SLA audit to Sheets",
        1960,
        "ReturnsSLA",
        '[["{rma_number}","{order_id}","{customer_email}","{product_sku}","{product_name}","{return_reason}","{package_condition}","{resolution}","{refund_amount}","{restock_action}","{refund_status}","{received_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // needs inspection -> delay
      e("3", "5", "output_2"), // good condition -> straight to resolution
      e("4", "5"), // after inspection -> resolution
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 511. RETURNS REFUND BOT (8 nodes)
  // ===================================================================

  "Returns Refund Bot": {
    summary:
      "A conversational bot that answers 'where is my refund?' inquiries with real-time Stripe data. A chat trigger extracts customer auth and RMA via AI, pulls Stripe refund state, routes on status (completed/in-progress/not-started), generates an AI precise status answer, delivers via channel, escalates overdue refunds to Zendesk, and logs inquiry patterns to Sheets.",
    nodes: [
      trigger("Webhook: Chat message — where is my refund?", "webhook_app", {
        source: "Chat Platform / Intercom / Zendesk Chat",
        event: "message.created",
        howItStarts:
          "A customer asks about the status of their refund via chat (e.g., 'where is my refund?', 'when will I get my money back?')",
        setup:
          "Connect your chat platform to POST incoming refund inquiry messages to the workflow URL. Ensure Stripe API and Zendesk credentials are configured.",
        inputFields: [],
      }),

      // 2. Customer auth + RMA extraction (AI)
      aiNode(
        "2",
        "AI extract customer & RMA details",
        280,
        "You are a refund inquiry parser. Extract the customer identity (email, name, order number) and any RMA or refund reference from the chat message regardless of format. Also identify the chat channel and conversation ID for response routing. Respond JSON: {customer_email: string, customer_name: string, order_id: string, rma_number: string, chat_channel: string, conversation_id: string, inquiry_text: string, urgency: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "rma_number", as: "rma_number", type: "string" },
          { field: "chat_channel", as: "chat_channel", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "inquiry_text", as: "inquiry_text", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Stripe refund state pull
      stripeNode(
        "3",
        "Pull Stripe refund status",
        520,
        "GET",
        "refunds?charge={stripe_charge_id}&limit=1",
        [],
        [
          { name: "data[0].id", customName: "refund_id", type: "string" },
          {
            name: "data[0].status",
            customName: "refund_status",
            type: "string",
          },
          {
            name: "data[0].amount",
            customName: "refund_amount",
            type: "number",
          },
          {
            name: "data[0].created",
            customName: "refund_created",
            type: "number",
          },
          {
            name: "data[0].currency",
            customName: "refund_currency",
            type: "string",
          },
        ],
      ),

      // 4. Status condition (completed / in-progress / not-started)
      n("4", "condition", "Refund completed?", 760, {
        conditionData: [
          {
            variable: "refund_status",
            value: "succeeded",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI precise status answer
      aiNode(
        "5",
        "AI compose refund status response",
        1000,
        "You are a friendly customer support agent. Compose a clear, empathetic response about the customer's refund status. If completed, confirm the amount and when it was processed. If in progress, give an estimated timeframe. If not started, explain next steps. Always be specific with amounts and dates. Never guess — only state what the data shows. Respond JSON: {response_message: string, estimated_arrival: string, needs_escalation: string}",
        "Customer: {customer_name} | Email: {customer_email} | Order: {order_id} | RMA: {rma_number} | Refund ID: {refund_id} | Status: {refund_status} | Amount: {refund_amount} {refund_currency} | Created: {refund_created} | Original inquiry: {inquiry_text}",
        [
          { field: "response_message", as: "bot_response", type: "string" },
          {
            field: "estimated_arrival",
            as: "estimated_arrival",
            type: "string",
          },
          { field: "needs_escalation", as: "needs_escalation", type: "string" },
        ],
      ),

      // 6. Channel delivery (chat reply)
      n("6", "sendMessage", "Deliver refund status via chat", 1240, {
        sendMessageDetails: {
          channel: "{chat_channel}",
          conversationId: "{conversation_id}",
          message: "{bot_response}",
        },
      }),

      // 7. Overdue escalation (Zendesk) — for non-completed refunds
      zendeskNode(
        "7",
        "Escalate overdue refund to Zendesk",
        1480,
        "Overdue Refund Escalation — RMA {rma_number}",
        "Customer {customer_name} ({customer_email}) has an unresolved refund.\\nOrder: {order_id} | RMA: {rma_number}\\nRefund Status: {refund_status} | Amount: {refund_amount} {refund_currency}\\nEstimated arrival: {estimated_arrival}\\nCustomer urgency: {urgency}\\nOriginal inquiry: {inquiry_text}",
        "high",
        [
          {
            name: "ticket.id",
            customName: "escalation_ticket_id",
            type: "string",
          },
        ],
        400,
      ),

      // 8. Inquiry pattern logging (Sheets)
      sheetsNode(
        "8",
        "Log inquiry pattern to Sheets",
        1720,
        "RefundInquiries",
        '[["{customer_email}","{order_id}","{rma_number}","{refund_status}","{refund_amount}","{urgency}","{needs_escalation}","{chat_channel}","{estimated_arrival}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // completed -> AI response
      e("4", "5", "output_2"), // not completed -> AI response (different data)
      e("5", "6"),
      e("6", "7"), // after delivery, escalate if overdue
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 512. REWARDS-BALANCE REMINDER AFTER PURCHASE (7 nodes)
  // ===================================================================

  "Rewards-Balance Reminder After Purchase": {
    summary:
      "Reminds customers of their rewards balance post-purchase and nudges them toward their next reward. A loyalty order webhook triggers points balance pull (after accrual), next-reward proximity condition, progress email for far-from-reward customers, urgency email for near-reward customers, send timing optimization, and redemption logging to Sheets.",
    nodes: [
      trigger(
        "Webhook: Loyalty order completed with points accrual",
        "webhook_app",
        {
          source: "Loyalty Platform / E-commerce",
          event: "order.loyalty_accrual",
          howItStarts:
            "A customer completes a purchase and loyalty points have been accrued to their account",
          setup:
            "Connect your loyalty platform to POST order completion events with accrual details (customer ID, email, points earned, new balance) to the workflow URL.",
          inputFields: [],
        },
      ),

      // 2. Points balance pull (after accrual)
      aiNode(
        "2",
        "AI extract order & points data",
        280,
        "You are a loyalty data parser. Extract the customer identity, order details, and updated points information from the webhook payload regardless of format. Calculate how close they are to the next reward tier. Respond JSON: {customer_email: string, customer_name: string, order_id: string, order_total: number, points_earned: number, points_balance: number, next_reward_threshold: number, next_reward_name: string, points_to_next_reward: number, proximity_pct: number, member_since: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "points_earned", as: "points_earned", type: "number" },
          { field: "points_balance", as: "points_balance", type: "number" },
          {
            field: "next_reward_threshold",
            as: "next_reward_threshold",
            type: "number",
          },
          { field: "next_reward_name", as: "next_reward_name", type: "string" },
          {
            field: "points_to_next_reward",
            as: "points_to_next_reward",
            type: "number",
          },
          { field: "proximity_pct", as: "proximity_pct", type: "number" },
          { field: "member_since", as: "member_since", type: "string" },
        ],
      ),

      // 3. Next-reward proximity condition
      n("3", "condition", "Within 20% of next reward?", 520, {
        conditionData: [
          {
            variable: "proximity_pct",
            value: "80",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Progress email (False path — far from reward)
      emailNode(
        "4",
        "Progress email: keep earning",
        760,
        "{customer_email}",
        "You earned {points_earned} points on your order, {customer_name}!",
        "Hi {customer_name},\\n\\nGreat news! Your recent order (${order_total}) earned you {points_earned} points.\\n\\nYour updated balance: {points_balance} points\\nNext reward: {next_reward_name} at {next_reward_threshold} points\\nPoints to go: {points_to_next_reward}\\n\\nKeep shopping to unlock your next reward!\\n\\nView your rewards: https://{store_url}/rewards",
        400,
      ),

      // 5. Urgency email (True path — close to reward)
      emailNode(
        "5",
        "Urgency email: almost there!",
        760,
        "{customer_email}",
        "You are SO close to {next_reward_name}, {customer_name}!",
        "Hi {customer_name},\\n\\nYou just earned {points_earned} points — and you are now only {points_to_next_reward} points away from {next_reward_name}!\\n\\nYour balance: {points_balance} / {next_reward_threshold} points ({proximity_pct}% there!)\\n\\nOne more purchase could unlock your reward. Do not miss out!\\n\\nBrowse now: https://{store_url}/shop?loyalty=nudge\\n\\nHappy shopping!",
      ),

      // 6. Send timing optimization delay
      n("6", "delayAction", "Optimal send timing (2h post-purchase)", 1000, {
        delayAction: { time: 2, timeFormat: "hours" },
      }),

      // 7. Redemption logging (Sheets)
      sheetsNode(
        "7",
        "Log redemption progress to Sheets",
        1240,
        "RewardsProgress",
        '[["{customer_email}","{customer_name}","{order_id}","{order_total}","{points_earned}","{points_balance}","{next_reward_name}","{next_reward_threshold}","{points_to_next_reward}","{proximity_pct}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "5", "output_1"), // close to reward -> urgency email
      e("3", "4", "output_2"), // far from reward -> progress email
      e("5", "6"),
      e("4", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 513. SKU DATA NORMALIZATION PIPELINE (9 nodes)
  // ===================================================================

  "SKU Data Normalization Pipeline": {
    summary:
      "Normalizes raw product/SKU data from file imports into a clean master catalog. A file import trigger parses staging data via runCode, maps columns with AI, normalizes values via runCode, scores quality, routes on quality score (auto-approve/review/reject), writes to master catalog, syncs to sales channels, and posts a Slack pipeline summary.",
    nodes: [
      trigger("Webhook: SKU file import uploaded", "webhook_app", {
        source: "Product Data Feed / ERP / CSV Upload",
        event: "file.sku_import",
        howItStarts:
          "A product data file (CSV, Excel, or JSON) is uploaded for catalog normalization",
        setup:
          "Connect your data import system to POST file upload events (file URL, format, source system, row count) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Staging parse (runCode)
      n("2", "runCode", "Parse staging data from file", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse uploaded file metadata and prepare for AI column mapping
const payload = JSON.parse(vars.webhook_payload || '{}');
const fileUrl = payload.file_url || payload.url || "";
const format = payload.format || "csv";
const sourceSystem = payload.source || "unknown";
const rowCount = parseInt(payload.row_count || payload.total_rows || "0", 10);
const sampleRows = payload.sample_rows || payload.preview || "[]";
const headers = payload.headers || payload.columns || [];
return {
  file_url: fileUrl,
  file_format: format,
  source_system: sourceSystem,
  row_count: rowCount,
  sample_data: typeof sampleRows === "string" ? sampleRows : JSON.stringify(sampleRows),
  raw_headers: typeof headers === "string" ? headers : JSON.stringify(headers),
  header_count: Array.isArray(headers) ? headers.length : 0,
  import_id: "IMP-" + Date.now()
};`,
          outputVariables: [
            { name: "file_url", type: "string" },
            { name: "file_format", type: "string" },
            { name: "source_system", type: "string" },
            { name: "row_count", type: "number" },
            { name: "sample_data", type: "string" },
            { name: "raw_headers", type: "string" },
            { name: "header_count", type: "number" },
            { name: "import_id", type: "string" },
          ],
        },
      }),

      // 3. AI column mapping
      aiNode(
        "3",
        "AI map columns to catalog schema",
        520,
        "You are a product data mapping specialist. Given raw column headers and sample data from a product file import, map each source column to the standard catalog schema: sku, product_name, description, category, subcategory, brand, price, cost, weight, dimensions, color, size, material, barcode, image_url, status. For each column, provide the best match and a confidence score. Respond JSON: {column_mapping: string, mapped_count: number, unmapped_columns: string, confidence_avg: number, requires_review: string}",
        "Source: {source_system} | Format: {file_format} | Headers: {raw_headers} | Sample data: {sample_data}",
        [
          { field: "column_mapping", as: "column_mapping", type: "string" },
          { field: "mapped_count", as: "mapped_count", type: "number" },
          { field: "unmapped_columns", as: "unmapped_columns", type: "string" },
          { field: "confidence_avg", as: "mapping_confidence", type: "number" },
          {
            field: "requires_review",
            as: "mapping_needs_review",
            type: "string",
          },
        ],
      ),

      // 4. Normalization (runCode)
      n("4", "runCode", "Normalize SKU values", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize product data values according to catalog standards
const mappedCount = parseInt(vars.mapped_count || "0", 10);
const rowCount = parseInt(vars.row_count || "0", 10);
const confidence = parseFloat(vars.mapping_confidence || "0");
const unmapped = vars.unmapped_columns || "";
// Quality scoring
const mappingScore = mappedCount > 0 ? (confidence / 100) * 40 : 0;
const coverageScore = mappedCount >= 10 ? 30 : (mappedCount / 10) * 30;
const volumeScore = rowCount > 0 ? Math.min(30, 30) : 0;
const qualityScore = Math.round(mappingScore + coverageScore + volumeScore);
const qualityGrade = qualityScore >= 80 ? "auto_approve" : qualityScore >= 50 ? "review" : "reject";
return {
  quality_score: qualityScore,
  quality_grade: qualityGrade,
  normalization_status: "completed",
  normalized_count: rowCount,
  mapping_coverage: mappedCount + "/" + vars.header_count + " columns mapped",
  unmapped_summary: unmapped || "none",
  processing_note: qualityGrade === "auto_approve" ? "High-quality import, auto-approved" : qualityGrade === "review" ? "Moderate quality, manual review required" : "Low quality, rejected — re-upload recommended"
};`,
          outputVariables: [
            { name: "quality_score", type: "number" },
            { name: "quality_grade", type: "string" },
            { name: "normalization_status", type: "string" },
            { name: "normalized_count", type: "number" },
            { name: "mapping_coverage", type: "string" },
            { name: "unmapped_summary", type: "string" },
            { name: "processing_note", type: "string" },
          ],
        },
      }),

      // 5. Quality score condition (auto-approve threshold)
      n("5", "condition", "Quality score >= 80 (auto-approve)?", 1000, {
        conditionData: [
          {
            variable: "quality_score",
            value: "80",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Master catalog write (True = auto-approve, False = review/reject)
      n("6", "hitApi", "Write to master catalog", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{catalog_api}/api/v1/imports/{import_id}/commit",
          headers: [
            { key: "Authorization", value: "Bearer {catalog_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "action", value: "{quality_grade}" },
            { key: "column_mapping", value: "{column_mapping}" },
            { key: "source", value: "{source_system}" },
            { key: "row_count", value: "{normalized_count}" },
          ],
          variables: [
            {
              name: "data.committed_rows",
              customName: "committed_rows",
              type: "number",
            },
            {
              name: "data.status",
              customName: "catalog_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Channel sync (push to connected sales channels)
      n("7", "hitApi", "Sync to sales channels", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{catalog_api}/api/v1/imports/{import_id}/sync-channels",
          headers: [
            { key: "Authorization", value: "Bearer {catalog_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "channels",
              value: '["shopify","amazon","google_shopping"]',
            },
          ],
          variables: [
            {
              name: "data.synced_channels",
              customName: "synced_channels",
              type: "number",
            },
            {
              name: "data.sync_status",
              customName: "sync_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Review/reject notification email (False path)
      emailNode(
        "8",
        "Email: Import needs review or rejected",
        1240,
        "{catalog_team_email}",
        "SKU Import [{quality_grade}] — {import_id} from {source_system}",
        "SKU Import Review Required\\n\\nImport ID: {import_id}\\nSource: {source_system} ({file_format})\\nRows: {row_count}\\nQuality Score: {quality_score}/100\\nGrade: {quality_grade}\\n\\nMapping Coverage: {mapping_coverage}\\nUnmapped Columns: {unmapped_summary}\\n\\nNote: {processing_note}\\n\\nPlease review in the catalog manager: https://{catalog_url}/imports/{import_id}",
        400,
      ),

      // 9. Slack pipeline summary
      slackNode(
        "9",
        "Slack: Pipeline summary",
        1720,
        ":package: SKU Pipeline Complete\\nImport: {import_id} ({source_system})\\nRows: {row_count} | Quality: {quality_score}/100 ({quality_grade})\\nMapping: {mapping_coverage}\\nCatalog: {catalog_status} | Channels: {sync_status}\\nNote: {processing_note}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // auto-approve -> catalog write
      e("5", "8", "output_2"), // review/reject -> notification email
      e("6", "7"), // catalog write -> channel sync
      e("8", "9"), // review email -> Slack summary
      e("7", "9"), // channel sync -> Slack summary
    ],
  },

  // ===================================================================
  // 514. SMS COUPON CODE DISTRIBUTOR (8 nodes)
  // ===================================================================

  "SMS Coupon Code Distributor": {
    summary:
      "Distributes unique coupon codes via SMS at scale with rate limiting and redemption tracking. A campaign trigger generates unique codes via runCode, pulls audience with consent verification, checks rate limits, composes personalized SMS messages, sends via Twilio in batches, monitors delivery and redemption, and posts a campaign summary to Slack.",
    nodes: [
      trigger("Manual: Launch SMS coupon campaign", "manual", {
        howItStarts:
          "A marketing team member launches an SMS coupon distribution campaign targeting a specific audience segment",
        setup:
          "Configure Twilio credentials, audience segment API, and coupon generation parameters before launching.",
        inputFields: [
          {
            name: "campaign_name",
            type: "string",
            example: "Spring Sale 2026",
          },
          { name: "discount_pct", type: "number", example: "20" },
          { name: "segment_id", type: "string", example: "seg_spring_active" },
          { name: "expiry_days", type: "number", example: "7" },
        ],
      }),

      // 2. Code generation at scale (runCode)
      n("2", "runCode", "Generate unique coupon codes", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate batch of unique coupon codes
const campaignName = vars.campaign_name || "PROMO";
const discountPct = parseInt(vars.discount_pct || "10", 10);
const expiryDays = parseInt(vars.expiry_days || "7", 10);
const prefix = campaignName.replace(/[^A-Z0-9]/gi, "").substring(0, 6).toUpperCase();
const batchSize = 1000;
const codes = [];
for (let i = 0; i < batchSize; i++) {
  const suffix = Math.random().toString(36).substring(2, 8).toUpperCase();
  codes.push(prefix + discountPct + suffix);
}
const expiryDate = new Date(Date.now() + expiryDays * 86400000).toISOString().split("T")[0];
return {
  codes_generated: batchSize,
  code_prefix: prefix + discountPct,
  sample_code: codes[0],
  expiry_date: expiryDate,
  discount_value: discountPct + "% off",
  codes_batch: JSON.stringify(codes)
};`,
          outputVariables: [
            { name: "codes_generated", type: "number" },
            { name: "code_prefix", type: "string" },
            { name: "sample_code", type: "string" },
            { name: "expiry_date", type: "string" },
            { name: "discount_value", type: "string" },
            { name: "codes_batch", type: "string" },
          ],
        },
      }),

      // 3. Audience + consent pull
      n("3", "hitApi", "Pull audience with SMS consent", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{audience_api}/api/v1/segments/{segment_id}/members?consent=sms&limit=1000",
          headers: [
            { key: "Authorization", value: "Bearer {audience_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.total_members",
              customName: "audience_size",
              type: "number",
            },
            {
              name: "data.consented_count",
              customName: "consented_count",
              type: "number",
            },
            {
              name: "data.suppressed_count",
              customName: "suppressed_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Rate-limit condition
      n("4", "condition", "Audience within rate limit?", 760, {
        conditionData: [
          {
            variable: "consented_count",
            value: "5000",
            comparison_operator: "LESS THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. SMS composition (runCode)
      n("5", "runCode", "Compose personalized SMS batch", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compose SMS message template for the batch
const campaignName = vars.campaign_name || "Sale";
const discountValue = vars.discount_value || "10% off";
const expiryDate = vars.expiry_date || "soon";
const sampleCode = vars.sample_code || "CODE123";
const consentedCount = parseInt(vars.consented_count || "0", 10);
const message = campaignName + "! Use code {unique_code} for " + discountValue + ". Expires " + expiryDate + ". Shop: https://{store_url}/sale Reply STOP to opt out";
return {
  sms_template: message,
  sms_length: message.length,
  estimated_sends: consentedCount,
  estimated_cost: (consentedCount * 0.0079).toFixed(2),
  batch_ready: consentedCount > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "sms_template", type: "string" },
            { name: "sms_length", type: "number" },
            { name: "estimated_sends", type: "number" },
            { name: "estimated_cost", type: "string" },
            { name: "batch_ready", type: "string" },
          ],
        },
      }),

      // 6. Twilio batch send
      n("6", "hitApi", "Twilio batch SMS send", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "From", value: "{twilio_phone}" },
            { key: "To", value: "{recipient_phone}" },
            { key: "Body", value: "{sms_template}" },
          ],
          variables: [
            { name: "sid", customName: "message_sid", type: "string" },
            { name: "status", customName: "send_status", type: "string" },
          ],
        },
      }),

      // 7. Delivery + redemption monitoring
      n("7", "hitApi", "Monitor delivery & redemption", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://{coupon_api}/api/v1/campaigns/{campaign_name}/stats",
          headers: [
            { key: "Authorization", value: "Bearer {coupon_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.delivered",
              customName: "delivered_count",
              type: "number",
            },
            { name: "data.failed", customName: "failed_count", type: "number" },
            {
              name: "data.redeemed",
              customName: "redeemed_count",
              type: "number",
            },
            {
              name: "data.delivery_rate",
              customName: "delivery_rate",
              type: "number",
            },
          ],
        },
      }),

      // 8. Campaign Slack summary
      slackNode(
        "8",
        "Slack: Campaign summary",
        1720,
        ":envelope: SMS Coupon Campaign Complete\\nCampaign: {campaign_name}\\nDiscount: {discount_value} | Expires: {expiry_date}\\nAudience: {audience_size} | Consented: {consented_count} | Suppressed: {suppressed_count}\\nSent: {estimated_sends} | Delivered: {delivered_count} | Failed: {failed_count}\\nRedemptions: {redeemed_count} | Delivery rate: {delivery_rate}%\\nEst. cost: ${estimated_cost}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // within rate limit -> compose
      e("4", "8", "output_2"), // over rate limit -> Slack alert only
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 515. SMS ORDER STATUS UPDATER (8 nodes)
  // ===================================================================

  "SMS Order Status Updater": {
    summary:
      "Sends real-time order status updates via SMS based on order lifecycle events. An order status webhook triggers SMS opt-in verification, status routing via runCode, confirmed SMS, shipped SMS, delivered SMS, satisfaction follow-up scheduling, and SMS ops failure alerting via Slack.",
    nodes: [
      trigger("Webhook: Order status change", "webhook_app", {
        source: "E-commerce Platform / OMS",
        event: "order.status_changed",
        howItStarts:
          "An order transitions to a new status (confirmed, shipped, out for delivery, delivered) triggering an SMS notification to the customer",
        setup:
          "Connect your OMS or e-commerce platform to POST order status change events (order ID, new status, customer phone, tracking info) to the workflow URL.",
        inputFields: [],
      }),

      // 2. SMS opt-in check condition
      aiNode(
        "2",
        "AI extract order status & customer",
        280,
        "You are an order status parser. Extract the order details, new status, customer information, and SMS opt-in status from the webhook payload regardless of format. Respond JSON: {order_id: string, customer_name: string, customer_phone: string, customer_email: string, new_status: string, tracking_number: string, carrier: string, sms_opted_in: string, estimated_delivery: string, order_items_summary: string}",
        "{webhook_payload}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "new_status", as: "new_status", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "sms_opted_in", as: "sms_opted_in", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          {
            field: "order_items_summary",
            as: "order_items_summary",
            type: "string",
          },
        ],
      ),

      // 3. SMS opt-in check
      n("3", "condition", "Customer opted in to SMS?", 520, {
        conditionData: [
          {
            variable: "sms_opted_in",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Status routing (runCode)
      n("4", "runCode", "Route by order status", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Route SMS content based on order status
const status = (vars.new_status || "").toLowerCase();
const name = vars.customer_name || "Customer";
const orderId = vars.order_id || "";
const tracking = vars.tracking_number || "";
const carrier = vars.carrier || "";
const eta = vars.estimated_delivery || "soon";
const items = vars.order_items_summary || "your items";
let smsBody, statusCategory;
if (status === "confirmed" || status === "processing") {
  statusCategory = "confirmed";
  smsBody = name + ", order " + orderId + " confirmed! We are preparing " + items + ". Track progress in your account.";
} else if (status === "shipped" || status === "in_transit") {
  statusCategory = "shipped";
  smsBody = name + ", order " + orderId + " shipped via " + carrier + "! Track: " + tracking + " | ETA: " + eta;
} else if (status === "delivered") {
  statusCategory = "delivered";
  smsBody = name + ", order " + orderId + " delivered! We hope you love " + items + ". Questions? Reply to this text.";
} else {
  statusCategory = "other";
  smsBody = name + ", update on order " + orderId + ": status is now " + status + ". Check your account for details.";
}
return {
  status_category: statusCategory,
  sms_body: smsBody,
  needs_followup: statusCategory === "delivered" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "status_category", type: "string" },
            { name: "sms_body", type: "string" },
            { name: "needs_followup", type: "string" },
          ],
        },
      }),

      // 5. Send order status SMS
      smsNode(
        "5",
        "Send order status SMS",
        1000,
        "{customer_phone}",
        "{sms_body}",
      ),

      // 6. Satisfaction follow-up scheduling (delay for delivered orders)
      n("6", "delayAction", "Wait 3 days for satisfaction follow-up", 1240, {
        delayAction: { time: 72, timeFormat: "hours" },
      }),

      // 7. Satisfaction follow-up SMS
      smsNode(
        "7",
        "Send satisfaction follow-up SMS",
        1480,
        "{customer_phone}",
        "Hi {customer_name}, how are you enjoying your order? Rate your experience 1-5 by replying to this text. Your feedback helps us improve! Reply STOP to opt out.",
      ),

      // 8. SMS ops failure alert (Slack) — for opt-out path
      slackNode(
        "8",
        "Slack: SMS ops failure alert",
        520,
        ":warning: SMS Delivery Issue\\nOrder: {order_id} | Status: {new_status}\\nCustomer: {customer_name} ({customer_phone})\\nSMS opted in: {sms_opted_in}\\nNote: Customer not opted in or delivery failed — check SMS consent records.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // opted in -> route status
      e("3", "8", "output_2"), // not opted in -> Slack alert
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"), // after followup -> log to Slack
    ],
  },

  // ===================================================================
  // 516. SALES REP ASSIST FOR B2B QUOTE OR CART (9 nodes)
  // ===================================================================

  "Sales Rep Assist for B2B Quote or Cart": {
    summary:
      "Assists sales reps when B2B customers abandon quotes or carts with full account context. A B2B cart/quote abandon trigger pulls pricing from Salesforce, looks up the rep and account, checks value thresholds, sends buyer recovery email, creates CRM follow-up task, emails the rep with a brief, alerts B2B sales Slack, and escalates failures to sales ops.",
    nodes: [
      trigger("Webhook: B2B cart or quote abandoned", "webhook_app", {
        source: "B2B E-commerce / CPQ",
        event: "quote.abandoned",
        howItStarts:
          "A B2B buyer abandons a cart or quote request, triggering sales rep notification and automated recovery",
        setup:
          "Connect your B2B e-commerce or CPQ system to POST abandoned quote/cart events (quote ID, account ID, buyer email, line items, total) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Quote/pricing pull (Salesforce)
      aiNode(
        "2",
        "AI extract quote & account data",
        280,
        "You are a B2B sales data parser. Extract the quote or cart details, buyer identity, account information, and pricing from the abandon event regardless of format. Respond JSON: {quote_id: string, account_id: string, account_name: string, buyer_email: string, buyer_name: string, buyer_title: string, line_items_summary: string, quote_total: number, currency: string, item_count: number, discount_applied: number, quote_expiry: string}",
        "{webhook_payload}",
        [
          { field: "quote_id", as: "quote_id", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          { field: "buyer_email", as: "buyer_email", type: "string" },
          { field: "buyer_name", as: "buyer_name", type: "string" },
          { field: "buyer_title", as: "buyer_title", type: "string" },
          {
            field: "line_items_summary",
            as: "line_items_summary",
            type: "string",
          },
          { field: "quote_total", as: "quote_total", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "discount_applied", as: "discount_applied", type: "number" },
          { field: "quote_expiry", as: "quote_expiry", type: "string" },
        ],
      ),

      // 3. Rep + account lookup (Salesforce)
      salesforceNode(
        "3",
        "Lookup rep & account in Salesforce",
        520,
        "GET",
        "sobjects/Account/{account_id}?fields=Name,OwnerId,Owner.Name,Owner.Email,Industry,AnnualRevenue,NumberOfEmployees,Type",
        [],
        [
          { name: "Owner.Name", customName: "rep_name", type: "string" },
          { name: "Owner.Email", customName: "rep_email", type: "string" },
          { name: "Industry", customName: "account_industry", type: "string" },
          {
            name: "AnnualRevenue",
            customName: "annual_revenue",
            type: "number",
          },
          { name: "Type", customName: "account_type", type: "string" },
          { name: "OwnerId", customName: "rep_sf_id", type: "string" },
        ],
      ),

      // 4. Value threshold condition
      n("4", "condition", "Quote value >= $5,000?", 760, {
        conditionData: [
          {
            variable: "quote_total",
            value: "5000",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Buyer recovery email
      emailNode(
        "5",
        "Send buyer recovery email",
        1000,
        "{buyer_email}",
        "Your quote for {account_name} is ready — {quote_id}",
        "Hi {buyer_name},\\n\\nWe noticed you were reviewing a quote for {account_name}.\\n\\nQuote Summary:\\n{line_items_summary}\\n\\nTotal: {quote_total} {currency} (discount: {discount_applied}%)\\nValid until: {quote_expiry}\\n\\nYour dedicated rep {rep_name} is available to answer any questions or adjust the quote to better fit your needs.\\n\\nResume your quote: https://{b2b_store_url}/quotes/{quote_id}\\n\\nBest regards,\\n{rep_name}",
      ),

      // 6. CRM follow-up task (Salesforce)
      salesforceNode(
        "6",
        "Create CRM follow-up task",
        1240,
        "POST",
        "sobjects/Task",
        [
          {
            key: "Subject",
            value:
              "Follow up: {buyer_name} abandoned quote {quote_id} (${quote_total})",
          },
          {
            key: "Description",
            value:
              "B2B quote abandoned. Account: {account_name} | Buyer: {buyer_name} ({buyer_title}) | Items: {item_count} | Total: {quote_total} {currency} | Discount: {discount_applied}% | Expiry: {quote_expiry}\\nLine items: {line_items_summary}",
          },
          { key: "OwnerId", value: "{rep_sf_id}" },
          { key: "WhatId", value: "{account_id}" },
          { key: "Priority", value: "High" },
          { key: "Status", value: "Not Started" },
          { key: "ActivityDate", value: "{tomorrow_date}" },
        ],
        [{ name: "id", customName: "task_id", type: "string" }],
      ),

      // 7. Rep brief email
      emailNode(
        "7",
        "Email rep with recovery brief",
        1480,
        "{rep_email}",
        "Quote Abandoned: {buyer_name} at {account_name} — ${quote_total}",
        "Hi {rep_name},\\n\\n{buyer_name} ({buyer_title}) at {account_name} abandoned a quote worth {quote_total} {currency}.\\n\\nQuote: {quote_id}\\nItems: {item_count} — {line_items_summary}\\nDiscount applied: {discount_applied}%\\nExpiry: {quote_expiry}\\n\\nAccount: {account_industry} | Revenue: ${annual_revenue} | Type: {account_type}\\n\\nA recovery email has been sent to the buyer. A follow-up task has been created in Salesforce (due tomorrow).\\n\\nQuote link: https://{b2b_store_url}/admin/quotes/{quote_id}",
      ),

      // 8. B2B sales Slack alert
      slackNode(
        "8",
        "Slack: B2B quote abandon alert",
        1720,
        ":briefcase: B2B Quote Abandoned\\nAccount: {account_name} ({account_industry})\\nBuyer: {buyer_name} ({buyer_title})\\nQuote: {quote_id} — ${quote_total} {currency} ({item_count} items)\\nRep: {rep_name}\\nDiscount: {discount_applied}% | Expiry: {quote_expiry}\\nRecovery email sent, CRM task created.",
      ),

      // 9. Sales ops failure escalation (for low-value or error path)
      emailNode(
        "9",
        "Sales ops: Low-value abandon log",
        1000,
        "{sales_ops_email}",
        "Low-value B2B abandon: {account_name} — ${quote_total}",
        "Low-value B2B quote abandoned (below $5,000 threshold).\\n\\nAccount: {account_name}\\nBuyer: {buyer_name} ({buyer_email})\\nQuote: {quote_id} — {quote_total} {currency}\\nItems: {line_items_summary}\\n\\nNo rep notification sent. Review if automated recovery is sufficient.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high value -> buyer recovery email
      e("4", "9", "output_2"), // low value -> sales ops log
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("9", "8"), // low-value also gets Slack summary
    ],
  },

  // ===================================================================
  // 517. SATISFACTION-TO-REVIEW REQUEST (7 nodes)
  // ===================================================================

  "Satisfaction-to-Review Request": {
    summary:
      "Converts positive satisfaction responses into product review requests with optimal timing. A positive CSAT/NPS webhook triggers order/product context pull, review destination selection via runCode, a spacing delay, review request email, loyalty incentive condition, and conversion tracking in Sheets.",
    nodes: [
      trigger("Webhook: Positive CSAT/NPS response received", "webhook_app", {
        source: "Survey Platform / CSAT Tool",
        event: "survey.response.positive",
        howItStarts:
          "A customer submits a positive satisfaction score (CSAT 4-5 or NPS 9-10), indicating they are a strong review candidate",
        setup:
          "Connect your survey platform to POST positive response events (customer email, score, order ID, product IDs) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Order/product context pull
      aiNode(
        "2",
        "AI extract satisfaction & order context",
        280,
        "You are a satisfaction data parser. Extract the customer identity, satisfaction score, associated order, and product details from the webhook payload regardless of format. Respond JSON: {customer_email: string, customer_name: string, satisfaction_score: number, score_type: string, order_id: string, product_name: string, product_id: string, purchase_date: string, feedback_comment: string, is_repeat_customer: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "satisfaction_score",
            as: "satisfaction_score",
            type: "number",
          },
          { field: "score_type", as: "score_type", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "purchase_date", as: "purchase_date", type: "string" },
          { field: "feedback_comment", as: "feedback_comment", type: "string" },
          {
            field: "is_repeat_customer",
            as: "is_repeat_customer",
            type: "string",
          },
        ],
      ),

      // 3. Review destination selection (runCode)
      n("3", "runCode", "Select review destination & compose request", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select the best review destination based on product and customer
const score = parseInt(vars.satisfaction_score || "0", 10);
const scoreType = vars.score_type || "CSAT";
const isRepeat = vars.is_repeat_customer === "yes";
const productName = vars.product_name || "our product";
// Prioritize Google for high scores, product page for others
let destination, reviewUrl;
if (score >= 9 || (scoreType === "CSAT" && score >= 5)) {
  destination = "google";
  reviewUrl = "https://g.page/{google_business_id}/review";
} else {
  destination = "product_page";
  reviewUrl = "https://{store_url}/products/" + (vars.product_id || "") + "#reviews";
}
const incentiveEligible = isRepeat ? "yes" : "no";
return {
  review_destination: destination,
  review_url: reviewUrl,
  incentive_eligible: incentiveEligible,
  request_tone: score >= 9 ? "enthusiastic" : "warm",
  review_prompt: "We would love to hear more about your experience with " + productName + "!"
};`,
          outputVariables: [
            { name: "review_destination", type: "string" },
            { name: "review_url", type: "string" },
            { name: "incentive_eligible", type: "string" },
            { name: "request_tone", type: "string" },
            { name: "review_prompt", type: "string" },
          ],
        },
      }),

      // 4. Spacing delay
      n("4", "delayAction", "Wait 48h before review request", 760, {
        delayAction: { time: 48, timeFormat: "hours" },
      }),

      // 5. Review request email
      emailNode(
        "5",
        "Send review request email",
        1000,
        "{customer_email}",
        "{customer_name}, we would love your review of {product_name}!",
        "Hi {customer_name},\\n\\nThank you for your fantastic {score_type} score of {satisfaction_score}! We are thrilled you had a great experience.\\n\\n{review_prompt}\\n\\nIt only takes a minute and helps others discover {product_name}:\\n{review_url}\\n\\nThank you for being a valued customer!\\n\\nWarmly,\\nThe Team",
      ),

      // 6. Loyalty incentive condition
      n("6", "condition", "Eligible for review incentive?", 1240, {
        conditionData: [
          {
            variable: "incentive_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Conversion tracking (Sheets)
      sheetsNode(
        "7",
        "Log review request to Sheets",
        1480,
        "ReviewRequests",
        '[["{customer_email}","{customer_name}","{satisfaction_score}","{score_type}","{product_name}","{product_id}","{order_id}","{review_destination}","{incentive_eligible}","{is_repeat_customer}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // incentive eligible -> log (incentive tracked)
      e("6", "7", "output_2"), // not eligible -> log (standard)
    ],
  },

  // ===================================================================
  // 518. SEASONAL REPLENISHMENT BY CLIMATE OR DATE (8 nodes)
  // ===================================================================

  "Seasonal Replenishment by Climate or Date": {
    summary:
      "Sends climate-aware or calendar-based replenishment reminders to customers by region. A weekly batch trigger resolves customer locations, maps climate/season via runCode, checks reorder suppression, routes on season type, sends climate-driven or calendar-driven emails, and logs regional performance to Sheets.",
    nodes: [
      trigger("Schedule: Weekly seasonal replenishment batch", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to evaluate customer replenishment needs based on their local climate and seasonal calendar",
        setup:
          "Configure customer database access, climate/weather API credentials, and product catalog for seasonal mapping.",
        inputFields: [],
      }),

      // 2. Customer location resolution
      n("2", "hitApi", "Resolve customer locations & purchase history", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{crm_api}/api/v1/customers/replenishment-eligible?include=location,last_purchase,product_preferences",
          headers: [
            { key: "Authorization", value: "Bearer {crm_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.customer_email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "data.customer_name",
              customName: "customer_name",
              type: "string",
            },
            { name: "data.city", customName: "customer_city", type: "string" },
            {
              name: "data.state",
              customName: "customer_state",
              type: "string",
            },
            {
              name: "data.country",
              customName: "customer_country",
              type: "string",
            },
            { name: "data.latitude", customName: "latitude", type: "number" },
            {
              name: "data.last_product",
              customName: "last_product",
              type: "string",
            },
            {
              name: "data.last_purchase_date",
              customName: "last_purchase_date",
              type: "string",
            },
            {
              name: "data.avg_reorder_days",
              customName: "avg_reorder_days",
              type: "number",
            },
          ],
        },
      }),

      // 3. Climate/season mapping (runCode)
      n("3", "runCode", "Map climate zone & season", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map customer location to climate zone and current season
const lat = parseFloat(vars.latitude || "40");
const country = vars.customer_country || "US";
const month = new Date().getMonth() + 1;
const isNorthern = lat >= 0;
// Determine season
let calendarSeason;
if (isNorthern) {
  calendarSeason = month <= 2 || month === 12 ? "winter" : month <= 5 ? "spring" : month <= 8 ? "summer" : "fall";
} else {
  calendarSeason = month <= 2 || month === 12 ? "summer" : month <= 5 ? "fall" : month <= 8 ? "winter" : "spring";
}
// Climate zone
let climateZone;
if (Math.abs(lat) <= 23.5) climateZone = "tropical";
else if (Math.abs(lat) <= 35) climateZone = "subtropical";
else if (Math.abs(lat) <= 55) climateZone = "temperate";
else climateZone = "cold";
// Determine routing: climate-driven vs calendar-driven
const useClimateDriven = climateZone === "tropical" || climateZone === "subtropical";
const lastProduct = vars.last_product || "seasonal essentials";
const reorderDays = parseInt(vars.avg_reorder_days || "90", 10);
const daysSincePurchase = Math.floor((Date.now() - new Date(vars.last_purchase_date || Date.now()).getTime()) / 86400000);
const shouldSuppress = daysSincePurchase < (reorderDays * 0.6);
return {
  calendar_season: calendarSeason,
  climate_zone: climateZone,
  season_routing: useClimateDriven ? "climate" : "calendar",
  days_since_purchase: daysSincePurchase,
  reorder_due: daysSincePurchase >= reorderDays ? "yes" : "no",
  suppress_send: shouldSuppress ? "yes" : "no",
  season_product_hint: calendarSeason + " " + lastProduct
};`,
          outputVariables: [
            { name: "calendar_season", type: "string" },
            { name: "climate_zone", type: "string" },
            { name: "season_routing", type: "string" },
            { name: "days_since_purchase", type: "number" },
            { name: "reorder_due", type: "string" },
            { name: "suppress_send", type: "string" },
            { name: "season_product_hint", type: "string" },
          ],
        },
      }),

      // 4. Reorder suppression check
      n("4", "condition", "Should suppress (too recent)?", 520, {
        conditionData: [
          {
            variable: "suppress_send",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Season routing condition
      n("5", "condition", "Climate-driven routing?", 1000, {
        conditionData: [
          {
            variable: "season_routing",
            value: "climate",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Climate-driven email (True path)
      emailNode(
        "6",
        "Climate-driven replenishment email",
        1240,
        "{customer_email}",
        "Time to restock for {climate_zone} {calendar_season}, {customer_name}!",
        "Hi {customer_name},\\n\\nBased on your location in {customer_city}, {customer_state}, the {climate_zone} {calendar_season} season is here — and it has been {days_since_purchase} days since your last purchase of {last_product}.\\n\\nWe have curated {calendar_season} essentials perfect for your climate:\\n{season_product_hint}\\n\\nShop seasonal picks: https://{store_url}/seasonal/{climate_zone}/{calendar_season}\\n\\nStay prepared!",
      ),

      // 7. Calendar-driven email (False path)
      emailNode(
        "7",
        "Calendar-driven replenishment email",
        1240,
        "{customer_email}",
        "{calendar_season} is here — time to refresh your {last_product}, {customer_name}!",
        "Hi {customer_name},\\n\\nHappy {calendar_season}! It has been {days_since_purchase} days since you last ordered {last_product} — the perfect time to stock up for the season.\\n\\nPopular {calendar_season} picks:\\n{season_product_hint}\\n\\nShop now: https://{store_url}/seasonal/{calendar_season}\\n\\nEnjoy the season!",
        400,
      ),

      // 8. Regional performance logging (Sheets)
      sheetsNode(
        "8",
        "Log regional performance to Sheets",
        1480,
        "SeasonalReplenishment",
        '[["{customer_email}","{customer_city}","{customer_state}","{customer_country}","{climate_zone}","{calendar_season}","{season_routing}","{days_since_purchase}","{last_product}","{suppress_send}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // not suppressed -> season routing
      e("4", "8", "output_2"), // suppressed -> log only
      e("5", "6", "output_1"), // climate-driven -> climate email
      e("5", "7", "output_2"), // calendar-driven -> calendar email
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 519. SECOND-PURCHASE ACCELERATOR (8 nodes)
  // ===================================================================

  "Second-Purchase Accelerator": {
    summary:
      "Accelerates the critical first-to-second purchase conversion with personalized timing and incentives. A first-order delivery trigger calculates the optimal purchase window via runCode, generates AI personalized recommendations, assigns incentives, generates unique codes, waits for the optimal window, sends the accelerator email, and logs incremental lift to Sheets.",
    nodes: [
      trigger("Webhook: First order delivered", "webhook_app", {
        source: "E-commerce Platform / Shipping",
        event: "order.delivered.first",
        howItStarts:
          "A customer's very first order is marked as delivered, triggering the second-purchase acceleration sequence",
        setup:
          "Connect your e-commerce platform to POST first-order delivery events (customer email, order details, products, delivery date) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Optimal window calculation (runCode)
      n("2", "runCode", "Calculate optimal purchase window", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate the optimal second-purchase window based on product category and price
const payload = JSON.parse(vars.webhook_payload || '{}');
const email = payload.customer_email || payload.email || "";
const name = payload.customer_name || payload.name || "Customer";
const orderId = payload.order_id || "";
const orderTotal = parseFloat(payload.total || payload.order_total || "0");
const products = payload.products || payload.line_items || [];
const firstProduct = products[0] || {};
const productName = firstProduct.name || firstProduct.title || "your purchase";
const category = (firstProduct.category || "general").toLowerCase();
// Optimal window by category
const windows = { "consumable": 14, "beauty": 21, "food": 7, "supplements": 25, "apparel": 30, "electronics": 45, "home": 35, "general": 21 };
const optimalDays = windows[category] || 21;
// Incentive by order value
let incentiveType, incentiveValue;
if (orderTotal >= 100) { incentiveType = "free_shipping"; incentiveValue = "Free shipping"; }
else if (orderTotal >= 50) { incentiveType = "percentage"; incentiveValue = "15% off"; }
else { incentiveType = "percentage"; incentiveValue = "10% off"; }
return {
  customer_email: email,
  customer_name: name,
  first_order_id: orderId,
  first_order_total: orderTotal,
  first_product_name: productName,
  product_category: category,
  optimal_days: optimalDays,
  incentive_type: incentiveType,
  incentive_value: incentiveValue
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "first_order_id", type: "string" },
            { name: "first_order_total", type: "number" },
            { name: "first_product_name", type: "string" },
            { name: "product_category", type: "string" },
            { name: "optimal_days", type: "number" },
            { name: "incentive_type", type: "string" },
            { name: "incentive_value", type: "string" },
          ],
        },
      }),

      // 3. AI personalized recommendations
      aiNode(
        "3",
        "AI generate personalized recommendations",
        520,
        "You are a product recommendation specialist. Based on the customer's first purchase, suggest 3 complementary products they would love as a second purchase. Make recommendations specific, helpful, and naturally connected to their first purchase. Respond JSON: {recommendation_1: string, recommendation_2: string, recommendation_3: string, recommendation_reason: string, email_subject: string, email_hook: string}",
        "Customer: {customer_name} | First product: {first_product_name} | Category: {product_category} | Order value: ${first_order_total} | Incentive: {incentive_value}",
        [
          { field: "recommendation_1", as: "rec_1", type: "string" },
          { field: "recommendation_2", as: "rec_2", type: "string" },
          { field: "recommendation_3", as: "rec_3", type: "string" },
          { field: "recommendation_reason", as: "rec_reason", type: "string" },
          { field: "email_subject", as: "acc_subject", type: "string" },
          { field: "email_hook", as: "email_hook", type: "string" },
        ],
      ),

      // 4. Unique code generation (runCode)
      n("4", "runCode", "Generate unique incentive code", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique discount code for second purchase
const prefix = "SECOND";
const suffix = Math.random().toString(36).substring(2, 8).toUpperCase();
const code = prefix + suffix;
const expiryDays = parseInt(vars.optimal_days || "21", 10) + 7;
const expiryDate = new Date(Date.now() + expiryDays * 86400000).toISOString().split("T")[0];
return {
  discount_code: code,
  code_expiry: expiryDate,
  code_expiry_days: expiryDays
};`,
          outputVariables: [
            { name: "discount_code", type: "string" },
            { name: "code_expiry", type: "string" },
            { name: "code_expiry_days", type: "number" },
          ],
        },
      }),

      // 5. Window delay
      n("5", "delayAction", "Wait for optimal purchase window", 1000, {
        delayAction: { time: "{optimal_days}", timeFormat: "days" },
      }),

      // 6. Purchase check before sending
      n("6", "condition", "Has customer made 2nd purchase?", 1240, {
        conditionData: [
          {
            variable: "second_purchase_made",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Accelerator email
      emailNode(
        "7",
        "Send second-purchase accelerator email",
        1480,
        "{customer_email}",
        "{acc_subject}",
        "Hi {customer_name},\\n\\n{email_hook}\\n\\nSince you loved {first_product_name}, we think you will enjoy these:\\n\\n1. {rec_1}\\n2. {rec_2}\\n3. {rec_3}\\n\\n{rec_reason}\\n\\nAs a thank-you for your first order, here is {incentive_value} on your next purchase:\\nCode: {discount_code} (expires {code_expiry})\\n\\nShop now: https://{store_url}/shop?code={discount_code}\\n\\nHappy shopping!",
      ),

      // 8. Incremental lift logging (Sheets)
      sheetsNode(
        "8",
        "Log incremental lift to Sheets",
        1720,
        "SecondPurchase",
        '[["{customer_email}","{customer_name}","{first_order_id}","{first_order_total}","{first_product_name}","{product_category}","{optimal_days}","{incentive_value}","{discount_code}","{code_expiry}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // no 2nd purchase yet -> send email
      e("6", "8", "output_2"), // already purchased -> log only
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 520. SESSION ABANDONMENT (8 nodes)
  // ===================================================================

  "Session Abandonment": {
    summary:
      "Re-engages users who abandon browsing sessions with context-aware follow-ups. A session end trigger aggregates browsing via runCode, ranks viewed products, routes by user type (customer vs prospect), sends personalized 'pick up where you left off' or browse recovery emails, checks for natural returns before sending, and logs natural return data to Sheets.",
    nodes: [
      trigger(
        "Webhook: Browsing session ended without conversion",
        "webhook_app",
        {
          source: "Analytics / Segment / GTM",
          event: "session.ended.no_conversion",
          howItStarts:
            "A user ends a browsing session without adding to cart or converting, after viewing multiple products",
          setup:
            "Connect your analytics platform to POST session-end events (session data, pages viewed, products browsed, user identity if known) to the workflow URL.",
          inputFields: [],
        },
      ),

      // 2. Session aggregation (runCode)
      n("2", "runCode", "Aggregate session data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate session browsing data for recovery email
const payload = JSON.parse(vars.webhook_payload || '{}');
const email = payload.email || payload.user_email || "";
const name = payload.name || payload.user_name || "there";
const pages = payload.pages_viewed || payload.page_views || [];
const products = payload.products_viewed || [];
const sessionDuration = parseFloat(payload.session_duration_minutes || "0");
const isCustomer = payload.is_customer === true || payload.is_customer === "yes";
// Rank products by engagement (time on page)
const ranked = products.sort((a, b) => (parseFloat(b.time_seconds || "0")) - (parseFloat(a.time_seconds || "0")));
const topProducts = ranked.slice(0, 3);
const topProductNames = topProducts.map(p => p.name || p.title || "Product").join(", ");
const topProductUrls = topProducts.map(p => p.url || "").join("|");
const topPrice = topProducts.length > 0 ? parseFloat(topProducts[0].price || "0") : 0;
return {
  user_email: email,
  user_name: name,
  is_existing_customer: isCustomer ? "yes" : "no",
  session_duration: sessionDuration,
  pages_count: pages.length || 0,
  products_viewed_count: products.length,
  top_product_names: topProductNames,
  top_product_urls: topProductUrls,
  top_product_price: topPrice,
  engagement_score: Math.min(100, Math.round(sessionDuration * 3 + products.length * 10))
};`,
          outputVariables: [
            { name: "user_email", type: "string" },
            { name: "user_name", type: "string" },
            { name: "is_existing_customer", type: "string" },
            { name: "session_duration", type: "number" },
            { name: "pages_count", type: "number" },
            { name: "products_viewed_count", type: "number" },
            { name: "top_product_names", type: "string" },
            { name: "top_product_urls", type: "string" },
            { name: "top_product_price", type: "number" },
            { name: "engagement_score", type: "number" },
          ],
        },
      }),

      // 3. Product ranking (AI)
      aiNode(
        "3",
        "AI rank & personalize recommendations",
        520,
        "You are a browse recovery specialist. Based on the products a user viewed and their engagement, create a personalized recovery message. For existing customers, use a 'welcome back' tone. For prospects, use a discovery tone. Respond JSON: {email_subject: string, email_body: string, cta_text: string, recovery_angle: string}",
        "User: {user_name} ({user_email}) | Customer: {is_existing_customer} | Session: {session_duration} min, {pages_count} pages | Products viewed: {products_viewed_count} | Top products: {top_product_names} | Engagement: {engagement_score}/100",
        [
          { field: "email_subject", as: "recovery_subject", type: "string" },
          { field: "email_body", as: "recovery_body", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
          { field: "recovery_angle", as: "recovery_angle", type: "string" },
        ],
      ),

      // 4. User type condition
      n("4", "condition", "Is existing customer?", 760, {
        conditionData: [
          {
            variable: "is_existing_customer",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Customer "pick up where you left off" email (True)
      emailNode(
        "5",
        "Customer: Pick up where you left off",
        1000,
        "{user_email}",
        "{recovery_subject}",
        "Welcome back, {user_name}!\\n\\n{recovery_body}\\n\\nProducts you were eyeing:\\n{top_product_names}\\n\\n{cta_text}\\n\\nShop your picks: https://{store_url}/browse-recovery",
      ),

      // 6. Prospect browse email (False)
      emailNode(
        "6",
        "Prospect: Browse recovery email",
        1000,
        "{user_email}",
        "{recovery_subject}",
        "Hi {user_name},\\n\\n{recovery_body}\\n\\nYou were checking out:\\n{top_product_names}\\n\\n{cta_text}\\n\\nExplore more: https://{store_url}/browse-recovery\\n\\nHappy browsing!",
        400,
      ),

      // 7. Pre-send return check delay
      n("7", "delayAction", "Wait 2h (natural return window)", 1240, {
        delayAction: { time: 2, timeFormat: "hours" },
      }),

      // 8. Natural return logging (Sheets)
      sheetsNode(
        "8",
        "Log session abandonment to Sheets",
        1480,
        "SessionAbandonment",
        '[["{user_email}","{user_name}","{is_existing_customer}","{session_duration}","{pages_count}","{products_viewed_count}","{top_product_names}","{engagement_score}","{recovery_angle}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // customer -> pick up where you left off
      e("4", "6", "output_2"), // prospect -> browse recovery
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 521. SHIPPING STATUS BOT (8 nodes)
  // ===================================================================

  "Shipping Status Bot": {
    summary:
      "A conversational bot that provides real-time shipping status from tracking data. A chat trigger extracts order/tracking info via AI, pulls real-time tracking data, routes on status (pre-ship/transit/delivered/exception), generates an AI conversational answer, delivers via channel, creates auto-tickets for exceptions via Zendesk, and alerts shipping ops on failures via Slack.",
    nodes: [
      trigger(
        "Webhook: Chat message — shipping status inquiry",
        "webhook_app",
        {
          source: "Chat Platform / Intercom / Zendesk Chat",
          event: "message.created",
          howItStarts:
            "A customer asks about their shipping status via chat (e.g., 'where is my order?', 'when will it arrive?', 'tracking update')",
          setup:
            "Connect your chat platform to POST incoming shipping inquiry messages to the workflow URL. Configure tracking API and Zendesk credentials.",
          inputFields: [],
        },
      ),

      // 2. Order/tracking extraction (AI)
      aiNode(
        "2",
        "AI extract order & tracking info",
        280,
        "You are a shipping inquiry parser. Extract the customer identity, order number, tracking number, and any specific concerns from the chat message regardless of format. Also identify the chat channel and conversation ID. Respond JSON: {customer_email: string, customer_name: string, order_id: string, tracking_number: string, chat_channel: string, conversation_id: string, specific_concern: string, inquiry_text: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "chat_channel", as: "chat_channel", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "specific_concern", as: "specific_concern", type: "string" },
          { field: "inquiry_text", as: "inquiry_text", type: "string" },
        ],
      ),

      // 3. Real-time tracking pull
      n("3", "hitApi", "Pull real-time tracking data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.shipengine.com/v1/tracking?carrier_code={carrier_code}&tracking_number={tracking_number}",
          headers: [
            { key: "API-Key", value: "{shipengine_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "status_code",
              customName: "tracking_status",
              type: "string",
            },
            {
              name: "status_description",
              customName: "status_description",
              type: "string",
            },
            {
              name: "carrier_status_description",
              customName: "carrier_status",
              type: "string",
            },
            {
              name: "estimated_delivery_date",
              customName: "estimated_delivery",
              type: "string",
            },
            {
              name: "actual_delivery_date",
              customName: "actual_delivery",
              type: "string",
            },
            {
              name: "events[0].description",
              customName: "latest_event",
              type: "string",
            },
            {
              name: "events[0].city_locality",
              customName: "latest_location",
              type: "string",
            },
            {
              name: "exception_description",
              customName: "exception_detail",
              type: "string",
            },
          ],
        },
      }),

      // 4. Status condition (exception detection)
      n("4", "condition", "Is there a shipping exception?", 760, {
        conditionData: [
          {
            variable: "tracking_status",
            value: "EX",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "tracking_status",
            value: "exception",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. AI conversational answer
      aiNode(
        "5",
        "AI compose shipping status response",
        1000,
        "You are a friendly shipping support agent. Compose a clear, helpful response about the shipment status. Be specific with locations, dates, and next steps. If there is an exception, acknowledge it empathetically and explain what is being done. Never guess delivery dates — only state confirmed data. Respond JSON: {response_message: string, action_taken: string, needs_human: string}",
        "Customer: {customer_name} | Order: {order_id} | Tracking: {tracking_number} | Status: {tracking_status} — {status_description} | Carrier: {carrier_status} | ETA: {estimated_delivery} | Delivered: {actual_delivery} | Latest: {latest_event} at {latest_location} | Exception: {exception_detail} | Concern: {specific_concern}",
        [
          { field: "response_message", as: "bot_response", type: "string" },
          { field: "action_taken", as: "action_taken", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
        ],
      ),

      // 6. Channel delivery
      n("6", "sendMessage", "Deliver shipping status via chat", 1240, {
        sendMessageDetails: {
          channel: "{chat_channel}",
          conversationId: "{conversation_id}",
          message: "{bot_response}",
        },
      }),

      // 7. Exception auto-ticket (Zendesk) — True path
      zendeskNode(
        "7",
        "Auto-ticket shipping exception (Zendesk)",
        1480,
        "Shipping Exception — Order {order_id} / {tracking_number}",
        "Shipping exception detected during customer inquiry.\\nCustomer: {customer_name} ({customer_email})\\nOrder: {order_id} | Tracking: {tracking_number}\\nStatus: {tracking_status} — {status_description}\\nException: {exception_detail}\\nLatest event: {latest_event} at {latest_location}\\nCustomer concern: {specific_concern}\\nBot response: {bot_response}\\nAction: {action_taken}",
        "high",
        [
          {
            name: "ticket.id",
            customName: "exception_ticket_id",
            type: "string",
          },
        ],
        400,
      ),

      // 8. Shipping ops failure alert (Slack)
      slackNode(
        "8",
        "Slack: Shipping ops alert",
        1720,
        ":package: Shipping Exception Alert\\nOrder: {order_id} | Tracking: {tracking_number}\\nStatus: {tracking_status} — {exception_detail}\\nCustomer: {customer_name} ({customer_email})\\nLatest: {latest_event} at {latest_location}\\nTicket: {exception_ticket_id}\\nAction: {action_taken}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // exception -> AI with exception context
      e("4", "5", "output_2"), // normal -> AI with normal context
      e("5", "6"),
      e("6", "7"), // exception -> Zendesk ticket
      e("7", "8"), // Zendesk -> Slack alert
    ],
  },

  // ===================================================================
  // 522. SHIPPING UPDATE WORKFLOW (8 nodes)
  // ===================================================================

  "Shipping Update Workflow": {
    summary:
      "Sends multi-channel shipping updates with carrier code normalization and exception handling. A tracking status webhook normalizes carrier codes via runCode, pulls channel preferences, routes by status, sends email updates, SMS updates via Twilio, creates exception tickets with proactive messages, and alerts shipping ops via Slack.",
    nodes: [
      trigger("Webhook: Tracking status update from carrier", "webhook_app", {
        source: "Shipping Carrier / ShipEngine / AfterShip",
        event: "tracking.status_updated",
        howItStarts:
          "A carrier pushes a tracking status update for a shipment, triggering customer notification via their preferred channel",
        setup:
          "Connect your shipping aggregator to POST tracking status events (tracking number, carrier, status, events, customer reference) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Carrier code normalization (runCode)
      n("2", "runCode", "Normalize carrier codes & extract data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize carrier tracking data into standard format
const payload = JSON.parse(vars.webhook_payload || '{}');
const tracking = payload.tracking_number || payload.tracking || "";
const rawCarrier = (payload.carrier || payload.carrier_code || "").toLowerCase();
const carrierMap = { "ups": "UPS", "usps": "USPS", "fedex": "FedEx", "dhl": "DHL", "dhl_express": "DHL Express", "ontrac": "OnTrac", "lasership": "LaserShip" };
const carrier = carrierMap[rawCarrier] || rawCarrier.toUpperCase();
const status = (payload.status || payload.status_code || "").toLowerCase();
const statusMap = { "pre_transit": "Order Processed", "in_transit": "In Transit", "out_for_delivery": "Out for Delivery", "delivered": "Delivered", "exception": "Exception", "return_to_sender": "Returned" };
const friendlyStatus = statusMap[status] || status;
const isException = status === "exception" || status === "return_to_sender";
const events = payload.events || payload.tracking_events || [];
const latestEvent = events[0] || {};
return {
  tracking_number: tracking,
  carrier_name: carrier,
  raw_status: status,
  friendly_status: friendlyStatus,
  is_exception: isException ? "yes" : "no",
  order_id: payload.order_id || payload.reference || "",
  customer_email: payload.customer_email || "",
  customer_phone: payload.customer_phone || "",
  customer_name: payload.customer_name || "Customer",
  estimated_delivery: payload.estimated_delivery || latestEvent.estimated_delivery || "TBD",
  latest_event: latestEvent.description || friendlyStatus,
  latest_location: latestEvent.city || latestEvent.location || "",
  tracking_url: "https://{store_url}/tracking/" + tracking
};`,
          outputVariables: [
            { name: "tracking_number", type: "string" },
            { name: "carrier_name", type: "string" },
            { name: "raw_status", type: "string" },
            { name: "friendly_status", type: "string" },
            { name: "is_exception", type: "string" },
            { name: "order_id", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "estimated_delivery", type: "string" },
            { name: "latest_event", type: "string" },
            { name: "latest_location", type: "string" },
            { name: "tracking_url", type: "string" },
          ],
        },
      }),

      // 3. Channel preference pull
      n("3", "hitApi", "Pull customer channel preferences", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{crm_api}/api/v1/customers/{customer_email}/preferences?fields=shipping_channel,sms_consent",
          headers: [
            { key: "Authorization", value: "Bearer {crm_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.preferred_channel",
              customName: "preferred_channel",
              type: "string",
            },
            {
              name: "data.sms_consent",
              customName: "sms_consent",
              type: "string",
            },
          ],
        },
      }),

      // 4. Status routing condition (exception vs normal)
      n("4", "condition", "Is shipping exception?", 760, {
        conditionData: [
          {
            variable: "is_exception",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Email update (normal path)
      emailNode(
        "5",
        "Email: Shipping status update",
        1000,
        "{customer_email}",
        "Shipping Update: {friendly_status} — Order {order_id}",
        "Hi {customer_name},\\n\\nHere is the latest on your shipment:\\n\\nOrder: {order_id}\\nCarrier: {carrier_name}\\nTracking: {tracking_number}\\nStatus: {friendly_status}\\nLatest: {latest_event} ({latest_location})\\nEstimated Delivery: {estimated_delivery}\\n\\nTrack your package: {tracking_url}\\n\\nThank you for your patience!",
      ),

      // 6. SMS update (Twilio) — normal path
      smsNode(
        "6",
        "SMS: Shipping status update (Twilio)",
        1240,
        "{customer_phone}",
        "{customer_name}, your order {order_id} is {friendly_status}. {latest_event} ({latest_location}). ETA: {estimated_delivery}. Track: {tracking_url}",
      ),

      // 7. Exception ticket + proactive message (True path)
      zendeskNode(
        "7",
        "Exception ticket + proactive message (Zendesk)",
        1000,
        "Shipping Exception — Order {order_id} ({carrier_name})",
        "Shipping exception detected for order {order_id}.\\nCustomer: {customer_name} ({customer_email})\\nCarrier: {carrier_name} | Tracking: {tracking_number}\\nStatus: {friendly_status}\\nLatest: {latest_event} at {latest_location}\\nAction needed: Investigate carrier exception and proactively contact customer.",
        "high",
        [
          {
            name: "ticket.id",
            customName: "exception_ticket_id",
            type: "string",
          },
        ],
        400,
      ),

      // 8. Shipping exception Slack alert
      slackNode(
        "8",
        "Slack: Shipping exception alert",
        1240,
        ":rotating_light: Shipping Exception\\nOrder: {order_id} | Carrier: {carrier_name}\\nTracking: {tracking_number}\\nStatus: {friendly_status} — {latest_event}\\nLocation: {latest_location}\\nCustomer: {customer_name} ({customer_email})\\nZendesk ticket: {exception_ticket_id}\\nAction required: Contact customer proactively.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"), // exception -> Zendesk ticket
      e("4", "5", "output_2"), // normal -> email update
      e("5", "6"), // email -> SMS
      e("7", "8"), // Zendesk -> Slack alert
      e("6", "8"), // SMS -> Slack (ops summary)
    ],
  },

  // ===================================================================
  // 523. SHOPIFY ABANDONED CHECKOUT RECOVERY (9 nodes)
  // ===================================================================

  "Shopify Abandoned Checkout Recovery": {
    summary:
      "Multi-touch Shopify abandoned checkout recovery with cart-value tiering and purchase checks between touches. A checkout abandon trigger pulls cart/customer data from Shopify, checks suppression, routes by cart value tier, sends reminder email 1, delays with purchase check, sends social proof email 2, sends incentive email 3 for high-value carts, and logs touch attribution to Sheets.",
    nodes: [
      trigger("Webhook: Shopify checkout abandoned", "webhook_app", {
        source: "Shopify",
        event: "checkouts/create",
        howItStarts:
          "A Shopify checkout is created but not completed within the abandonment window, triggering a multi-touch recovery sequence",
        setup:
          "Configure Shopify webhook for checkouts/create or use Shopify Flow to detect abandonment. Ensure email sending and Sheets credentials are configured.",
        inputFields: [],
      }),

      // 2. Cart/customer pull (Shopify)
      aiNode(
        "2",
        "AI extract checkout & customer data",
        280,
        "You are a Shopify checkout parser. Extract customer identity, cart contents, pricing, and abandonment context from the webhook payload regardless of format. Respond JSON: {customer_email: string, customer_name: string, cart_url: string, cart_total: number, item_count: number, items_summary: string, top_item_name: string, top_item_image: string, discount_codes: string, is_returning: string, checkout_id: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "cart_url", as: "cart_url", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "items_summary", as: "items_summary", type: "string" },
          { field: "top_item_name", as: "top_item_name", type: "string" },
          { field: "top_item_image", as: "top_item_image", type: "string" },
          { field: "discount_codes", as: "discount_codes", type: "string" },
          { field: "is_returning", as: "is_returning", type: "string" },
          { field: "checkout_id", as: "checkout_id", type: "string" },
        ],
      ),

      // 3. Suppression check (runCode)
      n("3", "runCode", "Check suppression rules", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if this checkout should be suppressed from recovery
const email = vars.customer_email || "";
const cartTotal = parseFloat(vars.cart_total || "0");
const hasDiscount = (vars.discount_codes || "").length > 0;
// Suppression rules
const isTestEmail = email.includes("test@") || email.includes("example.com");
const isTooLow = cartTotal < 10;
const shouldSuppress = isTestEmail || isTooLow;
// Cart value tier
let valueTier;
if (cartTotal >= 200) valueTier = "high";
else if (cartTotal >= 50) valueTier = "medium";
else valueTier = "low";
return {
  should_suppress: shouldSuppress ? "yes" : "no",
  value_tier: valueTier,
  has_existing_discount: hasDiscount ? "yes" : "no",
  suppression_reason: isTestEmail ? "test email" : isTooLow ? "cart value too low" : "none"
};`,
          outputVariables: [
            { name: "should_suppress", type: "string" },
            { name: "value_tier", type: "string" },
            { name: "has_existing_discount", type: "string" },
            { name: "suppression_reason", type: "string" },
          ],
        },
      }),

      // 4. Cart value tier condition
      n("4", "condition", "High-value cart (>= $200)?", 760, {
        conditionData: [
          {
            variable: "value_tier",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Email 1: Reminder
      emailNode(
        "5",
        "Email 1: Cart reminder",
        1000,
        "{customer_email}",
        "You left {top_item_name} in your cart, {customer_name}!",
        "Hi {customer_name},\\n\\nYou left some great items in your cart:\\n\\n{items_summary}\\n\\nTotal: ${cart_total}\\n\\nComplete your checkout: {cart_url}\\n\\nYour items are waiting!",
      ),

      // 6. Purchase check delay
      n("6", "delayAction", "Wait 24h then check purchase", 1240, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 7. Email 2: Social proof
      emailNode(
        "7",
        "Email 2: Social proof nudge",
        1480,
        "{customer_email}",
        "Others love {top_item_name} — still interested?",
        "Hi {customer_name},\\n\\nJust a quick note — {top_item_name} is one of our most popular items, and your cart is still saved.\\n\\n{items_summary}\\nTotal: ${cart_total}\\n\\nOur customers love these products (4.8/5 average rating). Do not miss out!\\n\\nComplete your order: {cart_url}",
      ),

      // 8. Email 3: Incentive (high-value only)
      emailNode(
        "8",
        "Email 3: Exclusive incentive (high-value)",
        1720,
        "{customer_email}",
        "A special offer for your cart, {customer_name}",
        "Hi {customer_name},\\n\\nWe really want you to love {top_item_name} and the rest of your selections.\\n\\n{items_summary}\\nOriginal total: ${cart_total}\\n\\nHere is an exclusive 10% off your cart — just for you:\\nCode: COMEBACK10\\n\\nComplete your order: {cart_url}?discount=COMEBACK10\\n\\nThis offer expires in 48 hours. Happy shopping!",
      ),

      // 9. Touch attribution logging (Sheets)
      sheetsNode(
        "9",
        "Log touch attribution to Sheets",
        1960,
        "CheckoutRecovery",
        '[["{customer_email}","{customer_name}","{checkout_id}","{cart_total}","{item_count}","{top_item_name}","{value_tier}","{is_returning}","{has_existing_discount}","{should_suppress}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high-value -> reminder email
      e("4", "5", "output_2"), // medium/low -> also reminder email
      e("5", "6"),
      e("6", "7"),
      e("7", "8"), // social proof -> incentive (for high-value)
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 524. SHOPIFY ORDER TO SLACK NOTIFICATION (6 nodes)
  // ===================================================================

  "Shopify Order to Slack Notification": {
    summary:
      "Posts rich Shopify order notifications to Slack with high-value badges. An order webhook extracts order data via runCode, checks for high-value badge condition, composes a Slack Block Kit message, posts to #new-orders, and sends a fallback error alert if posting fails.",
    nodes: [
      trigger("Webhook: Shopify new order", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "A new order is placed on Shopify, triggering a Slack notification for the team",
        setup:
          "Configure Shopify webhook for orders/create events. Set up Slack incoming webhook for your #new-orders channel.",
        inputFields: [],
      }),

      // 2. Data extraction (runCode)
      n("2", "runCode", "Extract order data for Slack", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and format order data for Slack notification
const payload = JSON.parse(vars.webhook_payload || '{}');
const orderNumber = payload.order_number || payload.name || "N/A";
const total = parseFloat(payload.total_price || "0");
const currency = payload.currency || "USD";
const customerEmail = (payload.customer || {}).email || payload.email || "unknown";
const customerName = ((payload.customer || {}).first_name || "") + " " + ((payload.customer || {}).last_name || "");
const itemCount = (payload.line_items || []).length;
const items = (payload.line_items || []).map(i => i.title + " x" + i.quantity).join(", ");
const isHighValue = total >= 500;
const financialStatus = payload.financial_status || "pending";
const shippingCountry = ((payload.shipping_address || {}).country) || "N/A";
const discountCodes = (payload.discount_codes || []).map(d => d.code).join(", ") || "none";
return {
  order_number: String(orderNumber),
  order_total: total,
  order_currency: currency,
  customer_email: customerEmail.trim(),
  customer_name: customerName.trim(),
  item_count: itemCount,
  items_list: items,
  is_high_value: isHighValue ? "yes" : "no",
  financial_status: financialStatus,
  shipping_country: shippingCountry,
  discount_codes: discountCodes,
  high_value_badge: isHighValue ? " :star: HIGH VALUE" : ""
};`,
          outputVariables: [
            { name: "order_number", type: "string" },
            { name: "order_total", type: "number" },
            { name: "order_currency", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "item_count", type: "number" },
            { name: "items_list", type: "string" },
            { name: "is_high_value", type: "string" },
            { name: "financial_status", type: "string" },
            { name: "shipping_country", type: "string" },
            { name: "discount_codes", type: "string" },
            { name: "high_value_badge", type: "string" },
          ],
        },
      }),

      // 3. High-value badge condition
      n("3", "condition", "Is high-value order (>= $500)?", 520, {
        conditionData: [
          {
            variable: "is_high_value",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Slack Block Kit message (high-value path with badge)
      slackNode(
        "4",
        "Slack: New order notification (#new-orders)",
        760,
        ":shopping_bags: New Order #{order_number}{high_value_badge}\\nCustomer: {customer_name} ({customer_email})\\nTotal: ${order_total} {order_currency} | Status: {financial_status}\\nItems ({item_count}): {items_list}\\nShipping to: {shipping_country}\\nDiscounts: {discount_codes}",
      ),

      // 5. Post confirmation (normal path)
      slackNode(
        "5",
        "Slack: Standard order posted",
        760,
        ":receipt: Order #{order_number}\\nCustomer: {customer_name} ({customer_email})\\nTotal: ${order_total} {order_currency} | Items: {item_count}\\n{items_list}\\nShip to: {shipping_country} | Discounts: {discount_codes}",
        400,
      ),

      // 6. Fallback error alert
      slackNode(
        "6",
        "Slack: Order notification error alert",
        1000,
        ":warning: Failed to process Shopify order notification\\nOrder: #{order_number} | Total: ${order_total}\\nCustomer: {customer_name} ({customer_email})\\nPlease check the workflow logs for errors.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // high-value -> badge notification
      e("3", "5", "output_2"), // normal -> standard notification
      e("4", "6"), // after high-value post -> error fallback
      e("5", "6"), // after standard post -> error fallback
    ],
  },

  // ===================================================================
  // 525. SHOPPING CART VALUE OPTIMIZER (8 nodes)
  // ===================================================================

  "Shopping Cart Value Optimizer": {
    summary:
      "Optimizes shopping cart value in real-time with AI-powered suggestions. A cart change trigger analyzes the cart via runCode, generates the single best AI optimization, routes by optimization type, nudges threshold proximity, suggests bundles, suggests cross-sells, and logs AOV lift to Sheets.",
    nodes: [
      trigger("Webhook: Cart updated with items", "webhook_app", {
        source: "E-commerce Platform / Shopify",
        event: "cart.updated",
        howItStarts:
          "A customer adds or removes items from their cart, triggering real-time value optimization analysis",
        setup:
          "Connect your e-commerce platform to POST cart update events (cart contents, customer session, pricing) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Cart analysis (runCode)
      n("2", "runCode", "Analyze cart for optimization", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze cart for optimization opportunities
const payload = JSON.parse(vars.webhook_payload || '{}');
const items = payload.items || payload.line_items || [];
const cartTotal = parseFloat(payload.total || "0");
const customerEmail = payload.email || payload.customer_email || "";
const customerName = payload.customer_name || "Shopper";
const sessionId = payload.session_id || "unknown";
// Calculate optimization opportunities
const freeShippingThreshold = 75;
const bundleThreshold = 3;
const gap = Math.max(0, freeShippingThreshold - cartTotal);
const itemNames = items.map(i => i.name || i.title || "Item").join(", ");
const categories = [...new Set(items.map(i => i.category || "general"))];
let optimizationType;
if (gap > 0 && gap <= 30) optimizationType = "threshold_nudge";
else if (items.length >= bundleThreshold) optimizationType = "bundle";
else optimizationType = "cross_sell";
return {
  customer_email: customerEmail,
  customer_name: customerName,
  session_id: sessionId,
  cart_total: cartTotal,
  item_count: items.length,
  item_names: itemNames,
  categories: categories.join(", "),
  free_shipping_gap: gap,
  optimization_type: optimizationType,
  has_optimization: "yes"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "session_id", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "item_count", type: "number" },
            { name: "item_names", type: "string" },
            { name: "categories", type: "string" },
            { name: "free_shipping_gap", type: "number" },
            { name: "optimization_type", type: "string" },
            { name: "has_optimization", type: "string" },
          ],
        },
      }),

      // 3. AI single best optimization
      aiNode(
        "3",
        "AI generate best optimization suggestion",
        520,
        "You are a cart optimization specialist. Based on the cart contents and optimization type, generate one highly specific, compelling suggestion. For threshold_nudge: suggest a specific product under the gap amount. For bundle: suggest a bundle deal with the existing items. For cross_sell: suggest one complementary product. Be concise and persuasive. Respond JSON: {suggestion_text: string, suggested_product: string, suggested_price: number, value_proposition: string, cta_text: string}",
        "Cart: {item_names} | Total: ${cart_total} | Categories: {categories} | Type: {optimization_type} | Free shipping gap: ${free_shipping_gap} | Items: {item_count}",
        [
          { field: "suggestion_text", as: "suggestion_text", type: "string" },
          {
            field: "suggested_product",
            as: "suggested_product",
            type: "string",
          },
          { field: "suggested_price", as: "suggested_price", type: "number" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      // 4. Optimization routing condition
      n("4", "condition", "Is threshold nudge opportunity?", 760, {
        conditionData: [
          {
            variable: "optimization_type",
            value: "threshold_nudge",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Threshold nudge widget (True path)
      emailNode(
        "5",
        "Threshold nudge notification",
        1000,
        "{customer_email}",
        "You are ${free_shipping_gap} away from free shipping!",
        "Hi {customer_name},\\n\\nYou are so close to free shipping! Add just ${free_shipping_gap} more to your cart.\\n\\nWe suggest: {suggested_product} (${suggested_price})\\n{value_proposition}\\n\\n{cta_text}\\n\\nShop: https://{store_url}/cart",
      ),

      // 6. Bundle suggestion (False -> check bundle)
      emailNode(
        "6",
        "Bundle / Cross-sell suggestion",
        1000,
        "{customer_email}",
        "Complete your look — a suggestion for your cart",
        "Hi {customer_name},\\n\\nBased on your cart ({item_names}):\\n\\n{suggestion_text}\\n\\nSuggested: {suggested_product} (${suggested_price})\\n{value_proposition}\\n\\n{cta_text}\\n\\nAdd to cart: https://{store_url}/cart",
        400,
      ),

      // 7. Cross-sell suggestion (widget/notification)
      n("7", "hitApi", "Push optimization to storefront widget", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{store_api}/api/v1/widgets/cart-optimization",
          headers: [
            { key: "Authorization", value: "Bearer {store_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "session_id", value: "{session_id}" },
            { key: "suggestion", value: "{suggestion_text}" },
            { key: "product", value: "{suggested_product}" },
            { key: "price", value: "{suggested_price}" },
            { key: "cta", value: "{cta_text}" },
          ],
          variables: [
            {
              name: "data.displayed",
              customName: "widget_displayed",
              type: "boolean",
            },
          ],
        },
      }),

      // 8. AOV lift logging (Sheets)
      sheetsNode(
        "8",
        "Log AOV lift to Sheets",
        1480,
        "AOVOptimization",
        '[["{customer_email}","{session_id}","{cart_total}","{item_count}","{optimization_type}","{suggested_product}","{suggested_price}","{free_shipping_gap}","{categories}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // threshold nudge -> nudge email
      e("4", "6", "output_2"), // bundle/cross-sell -> suggestion email
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 526. SIZE GUIDE BOT (8 nodes)
  // ===================================================================

  "Size Guide Bot": {
    summary:
      "A conversational size guide bot that helps customers find their perfect fit. A chat size query triggers product size chart pull, AI measurement collection, size recommendation, between-sizes condition for tradeoff explanation, quick-add-to-cart link generation, and styling support escalation for complex cases.",
    nodes: [
      trigger("Webhook: Chat message — size question", "webhook_app", {
        source: "Chat Platform / Widget",
        event: "message.created",
        howItStarts:
          "A customer asks a sizing question via chat (e.g., 'what size should I get?', 'I am 5 foot 10 what size?', 'size guide for this dress')",
        setup:
          "Connect your chat platform to POST sizing inquiry messages to the workflow URL. Ensure product catalog API access for size charts.",
        inputFields: [],
      }),

      // 2. Product size chart pull
      aiNode(
        "2",
        "AI extract product & measurement context",
        280,
        "You are a size inquiry parser. Extract the product being asked about, any measurements the customer provided (height, weight, chest, waist, hips, shoe size, etc.), their preferred fit, and the chat channel details from the message regardless of format. Respond JSON: {product_name: string, product_id: string, customer_name: string, height: string, weight: string, chest: string, waist: string, hips: string, shoe_size: string, preferred_fit: string, chat_channel: string, conversation_id: string, measurement_system: string}",
        "{webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "height", as: "customer_height", type: "string" },
          { field: "weight", as: "customer_weight", type: "string" },
          { field: "chest", as: "customer_chest", type: "string" },
          { field: "waist", as: "customer_waist", type: "string" },
          { field: "hips", as: "customer_hips", type: "string" },
          { field: "preferred_fit", as: "preferred_fit", type: "string" },
          { field: "chat_channel", as: "chat_channel", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          {
            field: "measurement_system",
            as: "measurement_system",
            type: "string",
          },
        ],
      ),

      // 3. AI measurement collection & size recommendation
      aiNode(
        "3",
        "AI size recommendation engine",
        520,
        "You are a professional sizing consultant. Based on the customer's measurements and the product's size chart, recommend the best size. If they are between sizes, say so and provide both options with tradeoffs. Consider their preferred fit (relaxed, regular, slim). If measurements are incomplete, ask for the most important missing one. Respond JSON: {recommended_size: string, confidence: string, is_between_sizes: string, size_up_option: string, size_down_option: string, fit_notes: string, missing_measurement: string, size_chart_summary: string}",
        "Product: {product_name} (ID: {product_id}) | Height: {customer_height} | Weight: {customer_weight} | Chest: {customer_chest} | Waist: {customer_waist} | Hips: {customer_hips} | Preferred fit: {preferred_fit} | System: {measurement_system}",
        [
          { field: "recommended_size", as: "recommended_size", type: "string" },
          { field: "confidence", as: "size_confidence", type: "string" },
          { field: "is_between_sizes", as: "is_between_sizes", type: "string" },
          { field: "size_up_option", as: "size_up", type: "string" },
          { field: "size_down_option", as: "size_down", type: "string" },
          { field: "fit_notes", as: "fit_notes", type: "string" },
          {
            field: "missing_measurement",
            as: "missing_measurement",
            type: "string",
          },
          {
            field: "size_chart_summary",
            as: "size_chart_summary",
            type: "string",
          },
        ],
      ),

      // 4. Size recommendation delivery
      n("4", "sendMessage", "Deliver size recommendation", 760, {
        sendMessageDetails: {
          channel: "{chat_channel}",
          conversationId: "{conversation_id}",
          message:
            "Based on your measurements, I recommend size {recommended_size} for {product_name}. {fit_notes}",
        },
      }),

      // 5. Between-sizes condition
      n("5", "condition", "Is customer between sizes?", 1000, {
        conditionData: [
          {
            variable: "is_between_sizes",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Tradeoff explanation (True path)
      n("6", "sendMessage", "Explain size tradeoffs", 1240, {
        sendMessageDetails: {
          channel: "{chat_channel}",
          conversationId: "{conversation_id}",
          message:
            "You are between sizes! Here are your options:\\n\\nSize up ({size_up}): Better for a relaxed, comfortable fit with more room.\\nSize down ({size_down}): Better for a slim, fitted look.\\n\\nFor {preferred_fit} fit, I would lean toward {recommended_size}. {fit_notes}",
        },
      }),

      // 7. Quick-add-to-cart link
      n("7", "sendMessage", "Send quick-add-to-cart link", 1480, {
        sendMessageDetails: {
          channel: "{chat_channel}",
          conversationId: "{conversation_id}",
          message:
            "Ready to go? Add {product_name} (size {recommended_size}) directly to your cart: https://{store_url}/cart/add?id={product_id}&size={recommended_size}",
        },
      }),

      // 8. Styling support escalation
      n("8", "sendMessage", "Offer live stylist escalation", 1720, {
        sendMessageDetails: {
          channel: "{chat_channel}",
          conversationId: "{conversation_id}",
          message:
            "Still unsure? I can connect you with one of our live stylists who can help with fit, styling, and more. Just say 'connect me' and I will transfer you right away!",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // between sizes -> tradeoff explanation
      e("5", "7", "output_2"), // clear size -> quick add to cart
      e("6", "7"), // after tradeoff -> add to cart
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 527. SIZE AND FIT GUIDE CHATBOT (8 nodes)
  // ===================================================================

  "Size and Fit Guide Chatbot": {
    summary:
      "A multi-channel chatbot providing comprehensive fit guidance with brand-size translation and fit prediction. A multi-channel fit query triggers product fit data and model measurements pull, AI measurement guidance, brand-size translation, fit prediction, between-sizes condition, recommendation delivery, and live stylist escalation.",
    nodes: [
      trigger("Webhook: Multi-channel fit inquiry", "webhook_app", {
        source: "Chat / WhatsApp / SMS / Web Widget",
        event: "message.fit_inquiry",
        howItStarts:
          "A customer asks about fit or sizing across any channel — chat, WhatsApp, SMS, or the website fit widget",
        setup:
          "Connect all customer messaging channels to POST fit inquiry messages to the workflow URL. Ensure product fit database and brand size chart APIs are configured.",
        inputFields: [],
      }),

      // 2. Product fit data + model measurements pull
      aiNode(
        "2",
        "AI extract fit inquiry & context",
        280,
        "You are a fit inquiry parser. Extract the product, customer measurements, their usual sizes in other brands, body type description, and channel details from the message regardless of format or channel. Respond JSON: {product_name: string, product_id: string, customer_name: string, channel_type: string, conversation_id: string, height: string, weight: string, body_type: string, usual_brand: string, usual_size_in_brand: string, chest: string, waist: string, hips: string, inseam: string, shoe_size: string, concern: string}",
        "{webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "channel_type", as: "channel_type", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "height", as: "customer_height", type: "string" },
          { field: "weight", as: "customer_weight", type: "string" },
          { field: "body_type", as: "body_type", type: "string" },
          { field: "usual_brand", as: "usual_brand", type: "string" },
          {
            field: "usual_size_in_brand",
            as: "usual_brand_size",
            type: "string",
          },
          { field: "chest", as: "customer_chest", type: "string" },
          { field: "waist", as: "customer_waist", type: "string" },
          { field: "hips", as: "customer_hips", type: "string" },
          { field: "inseam", as: "customer_inseam", type: "string" },
          { field: "concern", as: "fit_concern", type: "string" },
        ],
      ),

      // 3. AI measurement guidance + brand-size translation
      aiNode(
        "3",
        "AI measurement guidance & brand translation",
        520,
        "You are an expert fit consultant with knowledge of sizing across major brands. If the customer provided their size in another brand, translate it to this product's sizing. Guide them through any needed measurements. Consider their body type for fit prediction. Respond JSON: {measurement_guidance: string, needs_more_measurements: string, translated_size: string, translation_confidence: string, brand_comparison: string}",
        "Product: {product_name} | Height: {customer_height} | Weight: {customer_weight} | Body type: {body_type} | Usual brand: {usual_brand} size {usual_brand_size} | Chest: {customer_chest} | Waist: {customer_waist} | Hips: {customer_hips} | Inseam: {customer_inseam} | Concern: {fit_concern}",
        [
          {
            field: "measurement_guidance",
            as: "measurement_guidance",
            type: "string",
          },
          {
            field: "needs_more_measurements",
            as: "needs_more_info",
            type: "string",
          },
          { field: "translated_size", as: "translated_size", type: "string" },
          {
            field: "translation_confidence",
            as: "translation_confidence",
            type: "string",
          },
          { field: "brand_comparison", as: "brand_comparison", type: "string" },
        ],
      ),

      // 4. Fit prediction
      aiNode(
        "4",
        "AI fit prediction",
        760,
        "You are a fit prediction engine. Based on all available measurements, brand-size translation, and body type, predict the best fit. Rate your confidence. If between sizes, clearly state both options with specific tradeoffs for this product type. Include model comparison if helpful. Respond JSON: {predicted_size: string, fit_confidence: string, is_between_sizes: string, size_up_rationale: string, size_down_rationale: string, fit_description: string, model_comparison: string, return_risk: string}",
        "Product: {product_name} | Translated size: {translated_size} ({translation_confidence} confidence) | Brand: {usual_brand} {usual_brand_size} | Height: {customer_height} | Weight: {customer_weight} | Body: {body_type} | Chest: {customer_chest} | Waist: {customer_waist} | Hips: {customer_hips} | Inseam: {customer_inseam} | Concern: {fit_concern}",
        [
          { field: "predicted_size", as: "predicted_size", type: "string" },
          { field: "fit_confidence", as: "fit_confidence", type: "string" },
          { field: "is_between_sizes", as: "is_between_sizes", type: "string" },
          {
            field: "size_up_rationale",
            as: "size_up_rationale",
            type: "string",
          },
          {
            field: "size_down_rationale",
            as: "size_down_rationale",
            type: "string",
          },
          { field: "fit_description", as: "fit_description", type: "string" },
          { field: "model_comparison", as: "model_comparison", type: "string" },
          { field: "return_risk", as: "return_risk", type: "string" },
        ],
      ),

      // 5. Between-sizes condition
      n("5", "condition", "Is customer between sizes?", 1000, {
        conditionData: [
          {
            variable: "is_between_sizes",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Between-sizes detailed guidance (True path)
      n("6", "sendMessage", "Deliver between-sizes guidance", 1240, {
        sendMessageDetails: {
          channel: "{channel_type}",
          conversationId: "{conversation_id}",
          message:
            "Hi {customer_name}! For {product_name}, you are between sizes. Here is my recommendation:\\n\\nBest fit: {predicted_size} ({fit_confidence} confidence)\\n\\nSize up rationale: {size_up_rationale}\\nSize down rationale: {size_down_rationale}\\n\\n{fit_description}\\n\\n{brand_comparison}\\n\\n{model_comparison}",
        },
      }),

      // 7. Recommendation delivery (False path — clear size)
      n(
        "7",
        "sendMessage",
        "Deliver fit recommendation",
        1240,
        {
          sendMessageDetails: {
            channel: "{channel_type}",
            conversationId: "{conversation_id}",
            message:
              "Hi {customer_name}! For {product_name}, I recommend size {predicted_size} ({fit_confidence} confidence).\\n\\n{fit_description}\\n\\n{brand_comparison}\\n\\n{model_comparison}\\n\\nAdd to cart: https://{store_url}/cart/add?id={product_id}&size={predicted_size}",
          },
        },
        400,
      ),

      // 8. Live stylist escalation
      n("8", "sendMessage", "Offer live stylist escalation", 1480, {
        sendMessageDetails: {
          channel: "{channel_type}",
          conversationId: "{conversation_id}",
          message:
            "Want expert help? Our live stylists can assist with fit, alternative styles, and outfit planning. Say 'stylist' to connect with a fit specialist who can see the product details and your measurements. We are here to help you find the perfect fit!",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // between sizes -> detailed guidance
      e("5", "7", "output_2"), // clear size -> direct recommendation
      e("6", "8"), // between-sizes -> escalation offer
      e("7", "8"), // clear size -> escalation offer
    ],
  },
};
